/**
 * Admin view for the clinic Referral Program.
 * Rendered by the Scheduling dashboard as its own top-level tab (admin-only).
 *
 * Sections:
 *   1. New registrations — under review: approve with reminder-week count
 *      (required, 8–12), edit, delete.
 *   2. Weekly reminder reports: approve submitted weeks, mark paid
 *      (date + method) — identical mechanics to Adherence.
 *   3. Client update faxes: pick client → template (requisition / negative /
 *      positive / qualified) → preview PDF, test fax to any number, or send
 *      to the clinic's fax on file.
 *   4. Physician fax list: registered clinics + manual adds, CSV import,
 *      promo preview / test / per-clinic send / broadcast all (14-day
 *      courtesy skip), plus the recent fax job log.
 *
 * Fax delivery is SRFax-backed and reports standby until SRFAX_* env
 * credentials exist (GET /referrals/admin/fax/status).
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { RefreshCw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";
import { getToken } from "@/lib/consult/authSession";

const NAVY = "#1F4E79";
const GREEN = "#0F3B2E";
const MUTED = "#6B7A73";
const BORDER = "#E5E2DC";
const LABEL = "#2D5A4C";
const TEXT = "#1C2B26";
const RED = "#B4351B";
const SUCCESS = "#2E7D5B";
const AMBER = "#E58A2E";
const TEAL = "#0D6E63";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const rapi = axios.create({ baseURL: `${BACKEND}/api` });
rapi.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers["X-Auth-Token"] = t;
  return config;
});

const fromYMD = (ymd) => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}/.test(ymd)) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toYMD = (d) => (d ? format(d, "yyyy-MM-dd") : "");
const money = (n) => new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 0 }).format(n || 0);

// Available months: last 6 → current → next 3 (matches IncentivesPanel).
const buildMonths = () => {
  const list = [];
  const now = new Date();
  for (let i = -6; i <= 3; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString(undefined, { month: "short", year: "numeric" });
    list.push({ key, label });
  }
  return list;
};
const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/* same auto-format as the public /referrals form: 4165551234 -> (416) 555-1234 */
function fmtPhone(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 10);
  if (d.length > 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length > 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length > 0) return `(${d}`;
  return "";
}

/* ── CPSO register (register.cpso.on.ca) paste parsers ── */
const PROV = {
  ontario: "ON", "british columbia": "BC", alberta: "AB", saskatchewan: "SK",
  manitoba: "MB", quebec: "QC", "new brunswick": "NB", "nova scotia": "NS",
  "prince edward island": "PE", newfoundland: "NL", "newfoundland and labrador": "NL",
  "northwest territories": "NT", nunavut: "NU", yukon: "YT",
};

/* "Fletcher, David Mitchell\nCPSO#: 60796\nACTIVE" -> { name, cpso } */
function parseNameBlock(t) {
  const out = {};
  const cpsoM = t.match(/CPSO\s*#\s*:?\s*(\d{3,7})/i);
  const nameM = t.match(/([A-Za-zÀ-ÿ'. -]+),\s*([A-Za-zÀ-ÿ'. -]+)/);
  if (nameM) {
    const last = nameM[1].trim().replace(/\s+/g, " ");
    const first = nameM[2].split(/\n|CPSO|\bACTIVE\b/i)[0].trim().replace(/\s+/g, " ");
    if (last && first) out.name = `Dr. ${first} ${last}`;
  }
  if (cpsoM) out.cpso = cpsoM[1];
  return out;
}

/* "Primary Business Location:\n14 COLLEGE ST #203\nToronto Ontario M5G 1K2\n…Phone:\n(416) 595-7075\nFax:\n(416) 595-5508" */
function parseAddrBlock(t) {
  const out = {};
  const phM = t.match(/Phone:\s*\n?\s*([\d()\s.\-]{10,18})/i);
  const fxM = t.match(/Fax:\s*\n?\s*([\d()\s.\-]{10,18})/i);
  const emM = t.match(/([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i);
  if (phM) out.phone = fmtPhone(phM[1]);
  if (fxM) out.fax = fmtPhone(fxM[1]);
  if (emM) out.email = emM[1];

  const lines = t.split("\n").map((l) => l.trim()).filter(Boolean);
  const postalRe = /([A-Za-z]\d[A-Za-z])\s?(\d[A-Za-z]\d)\s*$/;
  const pIdx = lines.findIndex((l) => postalRe.test(l) && !/^\(?\d/.test(l));
  if (pIdx >= 0) {
    const pm = lines[pIdx].match(/^(.*?)\s*([A-Za-z]\d[A-Za-z])\s?(\d[A-Za-z]\d)\s*$/);
    out.postal = `${pm[2].toUpperCase()} ${pm[3].toUpperCase()}`;
    const rest = pm[1].trim(); // e.g. "Toronto Ontario"
    let prov = "", city = rest;
    for (const [name, abbr] of Object.entries(PROV)) {
      if (rest.toLowerCase().endsWith(name)) {
        prov = abbr; city = rest.slice(0, rest.length - name.length).trim(); break;
      }
    }
    if (!prov && /^[A-Za-z]{2}$/.test(rest.split(/\s+/).pop())) {
      prov = rest.split(/\s+/).pop().toUpperCase();
      city = rest.split(/\s+/).slice(0, -1).join(" ");
    }
    if (prov) out.province = prov;
    if (city && city !== rest) out.city = city;
    const locIdx = lines.findIndex((l) => /business location/i.test(l));
    const start = locIdx >= 0 && locIdx < pIdx ? locIdx + 1 : 0;
    const street = lines.slice(start, pIdx)
      .filter((l) => !/:\s*$/.test(l) && !/no information available/i.test(l) && !/^\(?\d{3}\)?[\s.\-]?\d{3}/.test(l))
      .join(", ");
    if (street) out.street = street;
  }
  return out;
}

/* reverse of the stored "street, city province postal" join — for editing */
function splitAddress(addr) {
  const out = { street: "", city: "", province: "", postal: "" };
  let rest = (addr || "").trim();
  const pm = rest.match(/([A-Za-z]\d[A-Za-z])\s?(\d[A-Za-z]\d)\s*$/);
  if (pm) { out.postal = `${pm[1].toUpperCase()} ${pm[2].toUpperCase()}`; rest = rest.slice(0, pm.index).trim(); }
  const words = rest.split(/[\s,]+/).filter(Boolean);
  if (words.length > 1 && /^[A-Za-z]{2}$/.test(words[words.length - 1])) {
    out.province = words.pop().toUpperCase();
    rest = words.join(" ");
  }
  const parts = rest.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length > 1) out.city = parts.pop();
  out.street = parts.join(", ");
  return out;
}
const fmtDate = (v) => {
  const d = fromYMD(String(v || "").slice(0, 10));
  return d ? format(d, "MMM d, yyyy") : "—";
};

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg.replace(/^Value error, /, "");
  return fallback;
}

// List view shows honorific + first + last name only (pasted names can carry
// several middle names); the edit form always keeps the full name.
const shortName = (full) => {
  const parts = String(full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 2) return full;
  const hasTitle = /^(dr\.?|doctor)$/i.test(parts[0]);
  const first = hasTitle ? parts[1] : parts[0];
  const last = parts[parts.length - 1];
  const core = first === last ? first : `${first} ${last}`;
  return hasTitle ? `${parts[0]} ${core}` : core;
};

// Fax-log recipient: promo faxes are addressed to physicians, so trim middle
// names the same way the physician list does — but never mangle clinic
// names or labels like "Test fax" (they carry no honorific).
const faxRecipient = (name) =>
  /^(dr\.?|doctor)\s/i.test(String(name || "").trim()) ? shortName(name) : name;

const FAX_TEMPLATES = [
  { key: "requisition", label: "Blood work requisition sent", desc: "attend any LifeLabs, pull up the requisition" },
  { key: "negative", label: "Negative result", desc: "no treatment needed; client claims $40 at hepcure.ca/patient" },
  { key: "positive", label: "Positive result", desc: "clinic calls us ASAP to qualify the client" },
  { key: "qualified", label: "Qualified for treatment", desc: "begin weekly compliance reminders" },
  { key: "denied", label: "Does not qualify", desc: "no action needed; client may call us with questions" },
];

const WEEK_STATUS = {
  pending: { label: "Pending", color: AMBER },
  approved: { label: "Approved", color: NAVY },
  paid: { label: "Paid", color: SUCCESS },
};
const WEEK_ORDER = { pending: 0, approved: 1, paid: 2 };

const EMPTY_PHYS = { name: "", clinic: "", fax: "", phone: "", email: "", street: "", city: "", province: "", postal: "", cpso: "", promo_rate: 75, specialty: "" };

// ── shared atoms ───────────────────────────────────────────────────────
const card = {
  background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: 20,
};
const secTitle = { fontSize: 13, fontWeight: 800, color: TEXT, marginBottom: 4 };
const secSub = { fontSize: 11.5, color: MUTED, marginBottom: 12 };
const rec = {
  border: `1px solid ${BORDER}`, borderRadius: 12, padding: "12px 14px", marginBottom: 10,
};
const acts = { display: "flex", gap: 6, marginTop: 8 };
const abtn = {
  flex: 1, borderRadius: 7, padding: "6px 4px", fontSize: 10.5, fontWeight: 700,
  border: `1px solid ${BORDER}`, background: "#fff", color: LABEL,
  textAlign: "center", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
};
const inp = {
  height: 40, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "0 12px",
  fontSize: 14, fontFamily: "inherit", background: "#fff", outline: "none", width: "100%",
};
const faxInput = { ...inp, height: 42, fontSize: 12, flex: 1, minWidth: 0 };
// Compact search field + scroll-capped list frame — long lists scroll inside a
// fixed-height window instead of stretching the page.
const searchInp = { ...inp, height: 34, fontSize: 12.5 };
const listScroll = { maxHeight: 420, overflowY: "auto", scrollbarWidth: "thin", overscrollBehavior: "none" };
const testBtn = {
  width: 118, flexShrink: 0, height: 42, borderRadius: 8, fontSize: 10.5, fontWeight: 700,
  border: `1.5px solid ${NAVY}`, background: "#fff", color: NAVY, cursor: "pointer", fontFamily: "inherit",
};
const sendBtn = { ...testBtn, background: NAVY, color: "#fff" };

function Section({ title, sub, children, testId, aside, flush }) {
  return (
    <div style={flush ? {} : { marginTop: 16, paddingTop: 14, borderTop: `1px solid ${BORDER}` }} data-testid={testId}>
      {title ? (
      <div style={{ ...secTitle, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span>{title}</span>
        {aside || null}
      </div>
      ) : null}
      {sub ? <div style={secSub}>{sub}</div> : null}
      {children}
    </div>
  );
}

// Framed group wrapper — splits this long tab into three clear zones.
function Group({ title, sub, children, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        marginTop: 18, border: `1.5px solid ${BORDER}`, borderRadius: 16,
        padding: "12px 12px 14px", background: "#FBFAF7",
      }}
    >
      <div style={{
        fontSize: 11, fontWeight: 800, letterSpacing: ".14em",
        textTransform: "uppercase", color: NAVY,
      }}>{title}</div>
      {sub ? <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>{sub}</div> : null}
      {children}
    </div>
  );
}

// Same 4-quadrant summary card the incentive teams use.
function TotalCard({ label, value, sub, strong = false, testId }) {
  return (
    <div data-testid={testId} style={{
      border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px",
      background: "#F9F8F6",
    }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: "0.18em",
        textTransform: "uppercase", color: LABEL,
      }}>{label}</div>
      <div style={{
        fontSize: 20, fontWeight: 700, marginTop: 4,
        color: strong ? GREEN : TEXT,
      }}>{value}</div>
      <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function DatePickerButton({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} style={{ ...abtn, flex: "none", minWidth: 110 }}>
          {value ? format(fromYMD(value), "MMM d, yyyy") : "Pick date"}
        </button>
      </PopoverTrigger>
      <PopoverContent style={{ width: "auto", padding: 0 }} align="start">
        <Calendar
          mode="single" selected={fromYMD(value)}
          onSelect={(d) => { onChange(toYMD(d)); setOpen(false); }}
        />
      </PopoverContent>
    </Popover>
  );
}

// ═══════════════════════════════════════════════════════════════════════
export default function ReferralsPanel({ refreshKey = 0 }) {
  // Standalone top-level Scheduling tab (admin-only) — owns its month/day selectors.
  const months = useMemo(() => buildMonths(), []);
  const [month, setMonth] = useState(currentMonthKey());
  const [day, setDay] = useState("all"); // "all" | YYYY-MM-DD
  const monthLabel = months.find((m) => m.key === month)?.label || month;

  const [clients, setClients] = useState([]);
  const [physicians, setPhysicians] = useState([]);
  const [physOptouts, setPhysOptouts] = useState([]);
  const [faxes, setFaxes] = useState([]);
  const [faxConfigured, setFaxConfigured] = useState(null);
  const [loading, setLoading] = useState(true);

  const [weeksInputs, setWeeksInputs] = useState({});  // clientId -> weeks string
  const [weeksErr, setWeeksErr] = useState({});        // clientId -> error msg
  const [editing, setEditing] = useState(null);        // client being edited
  const [payWeek, setPayWeek] = useState(null);        // { client, week }
  const [payDate, setPayDate] = useState(toYMD(new Date()));
  const [payMethod, setPayMethod] = useState("eft");
  const [editWeek, setEditWeek] = useState(null);      // { client, week }
  const [ewStatus, setEwStatus] = useState("pending");
  const [ewDate, setEwDate] = useState(toYMD(new Date()));
  const [ewMethod, setEwMethod] = useState("eft");
  const [retryFax, setRetryFax] = useState(null);      // fax doc being re-sent
  const [retryNumber, setRetryNumber] = useState("");
  const [pasteFallback, setPasteFallback] = useState(null); // "name" | "addr" — shown only if clipboard is blocked
  const [editingRow, setEditingRow] = useState(null);        // { source, id, name } — top form becomes the editor

  const [faxClient, setFaxClient] = useState("");
  const [faxTemplate, setFaxTemplate] = useState("requisition");
  const [testNumber, setTestNumber] = useState("(416) 639-9485");
  const [promoTestNumber, setPromoTestNumber] = useState("(416) 639-9485");
  // Resend rule for the promo broadcast: "days" skips numbers faxed in the
  // last N days (default 14), "all" skips every number ever faxed, "none"
  // re-faxes everyone.
  const [faxSuppression, setFaxSuppression] = useState("days");
  // Raw digits while typing (may be emptied mid-edit); clamped on use.
  const [faxSuppressionDays, setFaxSuppressionDays] = useState("14");
  const faxDaysNum = Math.max(1, Math.min(365, parseInt(faxSuppressionDays, 10) || 14));
  const [faxBusy, setFaxBusy] = useState(false);
  const [faxBusyWhat, setFaxBusyWhat] = useState(""); // which fax action is running
  // Busy-label pattern (same as the registration-sheet download): the tapped
  // button says what it's doing while the server renders/queues the PDF.
  const faxStart = (what) => { setFaxBusyWhat(what); setFaxBusy(true); };
  const faxDone = () => { setFaxBusy(false); setFaxBusyWhat(""); };
  const faxLbl = (what, busy, idle) => (faxBusy && faxBusyWhat === what ? busy : idle);
  const [specialties, setSpecialties] = useState([]);
  const [specOpen, setSpecOpen] = useState(false);
  const [specQ, setSpecQ] = useState("");
  const [specNew, setSpecNew] = useState("");
  const [specFilter, setSpecFilter] = useState(""); // fax-list filter ("" = all specialties)
  const [faxSyncing, setFaxSyncing] = useState(false);

  async function refreshFaxes() {
    setFaxSyncing(true);
    try {
      const { data } = await rapi.post("/referrals/admin/faxes/sync");
      if (data.updated) toast.success(`Updated ${data.updated} fax ${data.updated === 1 ? "status" : "statuses"}`);
      await load();
    } catch { toast.error("Couldn't refresh fax statuses"); }
    finally { setFaxSyncing(false); }
  }

  const [newPhys, setNewPhys] = useState({ ...EMPTY_PHYS });
  const [physMiss, setPhysMiss] = useState({});               // red highlight: {name, fax}
  const [physFaxConflict, setPhysFaxConflict] = useState(null); // dup-fax conflict details
  // Live duplicate guard: as soon as the CPSO licence number matches an
  // existing physician/clinic (any row except the one being edited), flag it
  // under the form so no time is wasted filling the rest.
  const cpsoDup = useMemo(() => {
    const v = (newPhys.cpso || "").trim().toLowerCase();
    if (!v) return null;
    return physicians.find((p) =>
      (p.cpso || "").trim().toLowerCase() === v &&
      !(editingRow && p.id === editingRow.id && p.source === editingRow.source)
    ) || null;
  }, [newPhys.cpso, physicians, editingRow]);
  const [physQ, setPhysQ] = useState("");
  const [physTab, setPhysTab] = useState("promoting");   // promoting | added | removed | active | inactive | optout

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, f, s] = await Promise.all([
        rapi.get("/referrals/admin/clients"),
        rapi.get("/referrals/admin/physicians"),
        rapi.get("/referrals/admin/faxes"),
        rapi.get("/referrals/admin/specialties"),
      ]);
      setClients(c.data.clients || []);
      setPhysicians(p.data.physicians || []);
      setPhysOptouts(p.data.optouts || []);
      setFaxes(f.data.faxes || []);
      setFaxConfigured(!!f.data.configured);
      setSpecialties(s.data.specialties || []);
    } catch (e) {
      toast.error(errText(e, "Couldn't load referrals data"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Payout summary for the quadrant card — the incentives summary endpoint
  // folds in paid referral weeks for team=referrals. Fetched here (not via
  // IncentivesPanel) because the ledger's /records 400s for this team and
  // would break its Promise.all. Refetches on month/day/refresh and whenever
  // clients change (a week was just approved/paid).
  const [paySummary, setPaySummary] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await rapi.get("/consultations/incentives/summary", {
          params: { team: "referrals", month, ...(day && day !== "all" ? { day } : {}) },
        });
        setPaySummary(data);
      } catch { /* keep previous */ }
    })();
  }, [month, day, refreshKey, clients]);

  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const matchQ = (c) =>
    !q ||
    `${c.first_name} ${c.last_name} ${c.code_display || ""} ${(c.clinic && c.clinic.clinic_name) || ""} ${c.office_name || ""}`
      .toLowerCase()
      .includes(q);

  const pendingRegs = useMemo(() => clients.filter((c) => c.status === "under_review" && matchQ(c)), [clients, q]);
  const approvedClients = useMemo(() => clients.filter((c) => c.status === "approved" && matchQ(c)), [clients, q]);
  const deniedClients = useMemo(() => clients.filter((c) => c.status === "denied" && matchQ(c)), [clients, q]);
  const pendingClients = useMemo(() => clients.filter((c) => c.status === "pending" && matchQ(c)), [clients, q]);
  const [approvingId, setApprovingId] = useState(null); // card id showing the inline medication-weeks step
  const [clientsTab, setClientsTab] = useState("qualified"); // qualified | denied
  const allWeeks = useMemo(() => {
    const rows = [];
    for (const c of approvedClients) {
      for (const w of c.weeks || []) {
        rows.push({ client: c, week: w });
      }
    }
    return rows.sort((a, b) =>
      (WEEK_ORDER[a.week.status] ?? 9) - (WEEK_ORDER[b.week.status] ?? 9) ||
      String(b.week.submitted_at || "").localeCompare(String(a.week.submitted_at || "")));
  }, [approvedClients]);
  const pendingWeeksCount = useMemo(
    () => allWeeks.filter((r) => r.week.status === "pending").length,
    [allWeeks]
  );

  // Weekly reports split: "Pending" (needs action: pending + approved) is the
  // default so paid weeks drop out of sight; "Paid" shows the paid history
  // (newest payment first). When everything is paid the list is empty/clean.
  const [weeksTab, setWeeksTab] = useState("open");
  const openWeeks = useMemo(() => allWeeks.filter((r) => r.week.status !== "paid"), [allWeeks]);
  const paidWeeks = useMemo(() =>
    allWeeks
      .filter((r) => r.week.status === "paid")
      .sort((a, b) => String(b.week.payment_date || b.week.submitted_at || "")
        .localeCompare(String(a.week.payment_date || a.week.submitted_at || ""))),
    [allWeeks]);
  const visibleWeeks = weeksTab === "paid" ? paidWeeks : openWeeks;

  // Physician fax list tabs: Promoting (manual entries still being faxed) ·
  // Added (signed up — manual flag, or fax matches a registered clinic) ·
  // Removed (filed away, never faxed) · Active / Inactive (self-registered
  // clinics) · No contact (opted out). Broadcasts only ever go to Promoting.
  const isManual = (p) => p.source === "manual";
  const isClinic = (p) => p.source === "clinic";
  const promotingPhys = useMemo(() => physicians.filter((p) => isManual(p) && !p.removed && !p.fax_opt_out && !p.joined), [physicians]);
  const addedPhys = useMemo(() => physicians.filter((p) => isManual(p) && !p.removed && !p.fax_opt_out && p.joined), [physicians]);
  const activePhys = useMemo(() => physicians.filter((p) => isClinic(p) && !p.removed && !p.fax_opt_out && !p.inactive), [physicians]);
  const inactivePhys = useMemo(() => physicians.filter((p) => isClinic(p) && !p.removed && !p.fax_opt_out && p.inactive), [physicians]);
  const removedPhys = useMemo(() => physicians.filter((p) => p.removed), [physicians]);
  const physSearch = (list, q) => {
    if (!q) return list;
    return list.filter((p) =>
      `${p.name} ${p.clinic} ${p.fax} ${p.phone || ""} ${p.code_display || ""}`.toLowerCase().includes(q));
  };
  const physQuery = physQ.trim().toLowerCase();
  const specOk = (list) => (specFilter ? list.filter((p) => (p.specialty || "") === specFilter) : list);
  const filteredPhys = useMemo(() => specOk(physSearch(promotingPhys, physQuery)), [promotingPhys, physQuery, specFilter]);
  const filteredAdded = useMemo(() => specOk(physSearch(addedPhys, physQuery)), [addedPhys, physQuery, specFilter]);
  const filteredActive = useMemo(() => specOk(physSearch(activePhys, physQuery)), [activePhys, physQuery, specFilter]);
  const filteredInactive = useMemo(() => specOk(physSearch(inactivePhys, physQuery)), [inactivePhys, physQuery, specFilter]);
  const filteredRemoved = useMemo(() => specOk(physSearch(removedPhys, physQuery)), [removedPhys, physQuery, specFilter]);
  // No contact merges opted-out physicians/clinics with any orphan numbers
  // that exist only on the do-not-contact list (e.g. added from the fax log).
  const optoutRows = useMemo(() => {
    const opted = physicians.filter((p) => p.fax_opt_out && !p.removed);
    const known = new Set(opted.map((p) => p.fax_raw));
    const orphans = physOptouts
      .filter((o) => !known.has(o.fax_raw))
      .map((o) => ({ source: "optout", id: `opt-${o.fax_raw}`, code_display: null,
                     name: o.name, clinic: "", fax: o.fax, fax_raw: o.fax_raw,
                     phone: "", last_faxed_at: null, fax_opt_out: true }));
    return [...opted, ...orphans];
  }, [physicians, physOptouts]);
  const filteredOptouts = useMemo(() => specOk(physSearch(optoutRows, physQuery)), [optoutRows, physQuery, specFilter]);

  // ── fax activity: month + status filters ──
  const FAX_STATUS_COLORS = { Sent: SUCCESS, Failed: RED, "In Progress": "#9A6208" };
  const FAX_STATUS_LABEL = { Sent: "Sent", Failed: "Failed", "In Progress": "Pending" };
  const [faxStatusFilter, setFaxStatusFilter] = useState(null);
  const [faxCat, setFaxCat] = useState("all");   // all | patient | promotion
  const [faxQ, setFaxQ] = useState("");
  // the shared Day + Month selectors at the top of the Rewards tab drive
  // this list (no separate fax month dropdown — keeps the row clean)
  const monthFaxes = useMemo(() => {
    let list = faxes.filter((f) => String(f.created_at || "").startsWith(month));
    if (day && day !== "all") list = list.filter((f) => String(f.created_at || "").startsWith(day));
    return list;
  }, [faxes, month, day]);
  // Days on which faxes were actually sent (selected month).
  const faxDays = useMemo(() => {
    const set = new Set();
    faxes.forEach((f) => {
      const d = String(f.created_at || "").slice(0, 10);
      if (d.startsWith(month)) set.add(d);
    });
    return [...set];
  }, [faxes, month]);
  // Day dropdown = payment days ∪ fax days (desc) so the fax list can be
  // filtered by the dates faxes were actually sent, not just payout days.
  const dayOptions = useMemo(() => (
    [...new Set([...(paySummary?.days_with_records || []), ...faxDays])].sort().reverse()
  ), [paySummary, faxDays]);
  const faxCounts = useMemo(() => ({
    Sent: monthFaxes.filter((f) => f.status === "Sent").length,
    Failed: monthFaxes.filter((f) => f.status === "Failed").length,
    "In Progress": monthFaxes.filter((f) => f.status === "In Progress").length,
  }), [monthFaxes]);
  // categories: promo broadcasts/tests vs patient updates (requisition, results…)
  const faxCategory = (f) => (f.template === "promo" ? "promotion" : "patient");
  const catCounts = useMemo(() => ({
    all: monthFaxes.length,
    patient: monthFaxes.filter((f) => faxCategory(f) === "patient").length,
    promotion: monthFaxes.filter((f) => faxCategory(f) === "promotion").length,
  }), [monthFaxes]);
  const visibleFaxes = useMemo(() => {
    let list = monthFaxes;
    if (faxCat !== "all") list = list.filter((f) => faxCategory(f) === faxCat);
    if (faxStatusFilter) list = list.filter((f) => f.status === faxStatusFilter);
    const q = faxQ.trim().toLowerCase();
    if (q) {
      const qDigits = q.replace(/\D/g, "");
      list = list.filter((f) => {
        const hay = `${f.template} ${f.client_name || ""} ${f.client_code_display || ""} ${f.to_name || ""} ${f.account_code || ""} ${f.srfax_id || ""} ${fmtDate(f.created_at)}`.toLowerCase();
        if (hay.includes(q)) return true;
        if (qDigits && String(f.to_number || "").replace(/\D/g, "").includes(qDigits)) return true;
        return false;
      });
    }
    return list;
  }, [monthFaxes, faxCat, faxStatusFilter, faxQ]);
  const faxFiltersNeutral = faxStatusFilter === null && faxCat === "all" && !faxQ.trim();

  // ── height stabilization: filtering a list must not shrink the page ──
  const faxRowsRef = useRef(null);
  const [faxRowsMinH, setFaxRowsMinH] = useState(0);
  useEffect(() => {
    if (faxFiltersNeutral && faxRowsRef.current) {
      setFaxRowsMinH(faxRowsRef.current.offsetHeight);
    }
  }, [visibleFaxes.length, faxFiltersNeutral, month, day]);
  const physTableRef = useRef(null);
  const [physTableMinH, setPhysTableMinH] = useState(0);
  useEffect(() => {
    if (physTableRef.current) {
      setPhysTableMinH(physTableRef.current.offsetHeight);
    }
  }, [filteredPhys.length, filteredAdded.length, filteredRemoved.length, filteredActive.length, filteredInactive.length, filteredOptouts.length, physTab]);

  const faxClientObj = useMemo(
    () => clients.find((c) => c.code === faxClient) || null,
    [clients, faxClient]
  );

  // ── registrations ──
  async function approveRegistration(c) {
    const raw = (weeksInputs[c.id] || "").trim();
    if (!raw) {
      setWeeksErr((m) => ({ ...m, [c.id]: "Enter the number of reminder weeks (8–12)" }));
      return;
    }
    try {
      await rapi.post(`/referrals/admin/clients/${c.id}/approve`, { total_weeks: Number(raw) });
      toast.success(`${c.first_name} ${c.last_name} qualified — ${raw} reminder weeks`);
      setWeeksErr((m) => ({ ...m, [c.id]: null }));
      setApprovingId(null);
      load();
    } catch (e) {
      const msg = errText(e, "Couldn't approve");
      setWeeksErr((m) => ({ ...m, [c.id]: msg }));
      toast.error(msg);
    }
  }

  // Deny = our disposition: the client didn't meet program criteria. One tap
  // + toast; the "does not qualify" notice is faxed from the Denied list.
  async function denyRegistration(c) {
    try {
      await rapi.post(`/referrals/admin/clients/${c.id}/deny`, {});
      if (faxClient === c.code) setFaxClient("");
      toast.success(`${c.first_name} ${c.last_name} denied — does not qualify`);
      load();
    } catch (e) { toast.error(errText(e, "Couldn't deny")); }
  }

  // Pending = middle disposition: held for follow-up, neither qualified nor denied.
  async function pendRegistration(c) {
    try {
      await rapi.post(`/referrals/admin/clients/${c.id}/pend`, {});
      if (faxClient === c.code) setFaxClient("");
      toast.success(`${c.first_name} ${c.last_name} moved to Pending`);
      setClientsTab("pending");
      load();
    } catch (e) { toast.error(errText(e, "Couldn't move to pending")); }
  }

  async function reopenRegistration(c) {
    try {
      await rapi.post(`/referrals/admin/clients/${c.id}/reopen`, {});
      toast.success(`${c.first_name} ${c.last_name} moved back to review`);
      load();
    } catch (e) { toast.error(errText(e, "Couldn't move back")); }
  }

  // Approve is a two-tap inline step: tap Approve → enter medication weeks → Confirm
  function renderApproveStep(c) {
    return (
      <div style={{ display: "flex", gap: 6, marginTop: 6, flex: "1 1 100%" }} data-testid={`approve-step-${c.id}`}>
        <input
          placeholder="Weeks" inputMode="numeric"
          value={weeksInputs[c.id] || ""}
          onChange={(e) => {
            setWeeksInputs((m) => ({ ...m, [c.id]: e.target.value.replace(/\D/g, "").slice(0, 2) }));
            setWeeksErr((m2) => ({ ...m2, [c.id]: null }));
          }}
          data-testid={`weeks-input-${c.id}`}
          className="ref-mini"
          style={{ flex: 1, minWidth: 0, color: NAVY, border: `1.5px solid ${weeksErr[c.id] ? RED : BORDER}` }}
        />
        <button className="ref-mini" style={{ flex: 1, background: GREEN, color: "#fff", border: `1px solid ${GREEN}` }}
          onClick={() => approveRegistration(c)} data-testid={`approve-confirm-${c.id}`}>Confirm</button>
        <button className="ref-mini" style={{ flex: 1 }} onClick={() => setApprovingId(null)} data-testid={`approve-cancel-${c.id}`}>✕</button>
      </div>
    );
  }

  async function deleteClient(c) {
    if (!window.confirm(`Delete the registration for ${c.first_name} ${c.last_name}? This can't be undone.`)) return;
    try {
      await rapi.delete(`/referrals/admin/clients/${c.id}`);
      if (faxClient === c.code) setFaxClient("");  // was selected for faxing — clear it
      toast.success("Registration deleted");
      load();
    } catch (e) { toast.error(errText(e, "Couldn't delete")); }
  }

  async function saveEdit() {
    try {
      await rapi.patch(`/referrals/admin/clients/${editing.id}`, {
        first_name: editing.first_name, last_name: editing.last_name,
        dob: editing.dob, health_card: editing.health_card,
        total_weeks: editing.total_weeks ? Number(editing.total_weeks) : undefined,
      });
      toast.success("Registration updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(errText(e, "Couldn't save")); }
  }

  // ── weeks ──
  async function approveWeek(c, w) {
    try {
      await rapi.patch(`/referrals/admin/clients/${c.id}/weeks/${w.n}`, { action: "approve" });
      toast.success(`Week ${w.n} approved & paid ${money(75)} — ${c.first_name} ${c.last_name} (today, e-transfer)`);
      load();
    } catch (e) { toast.error(errText(e, "Couldn't approve week")); }
  }

  async function markPaid() {
    if (!payDate) { toast.error("Pick the payment date"); return; }
    try {
      await rapi.patch(`/referrals/admin/clients/${payWeek.client.id}/weeks/${payWeek.week.n}`, {
        action: "mark_paid", payment_date: payDate, payment_method: payMethod,
      });
      toast.success(`Week ${payWeek.week.n} marked paid — ${money(75)}`);
      setPayWeek(null);
      load();
    } catch (e) { toast.error(errText(e, "Couldn't mark paid")); }
  }

  function openWeekEdit(c, w) {
    setEditWeek({ client: c, week: w });
    setEwStatus(w.status || "pending");
    setEwDate(w.payment_date || toYMD(new Date()));
    setEwMethod(w.payment_method || "eft");
  }

  async function saveWeekEdit() {
    if (ewStatus === "paid" && !ewDate) { toast.error("Pick the payment date"); return; }
    try {
      await rapi.patch(`/referrals/admin/clients/${editWeek.client.id}/weeks/${editWeek.week.n}/edit`, {
        status: ewStatus,
        payment_date: ewStatus === "paid" ? ewDate : undefined,
        payment_method: ewStatus === "paid" ? ewMethod : undefined,
      });
      toast.success(`Week ${editWeek.week.n} updated — ${editWeek.client.first_name} ${editWeek.client.last_name}`);
      setEditWeek(null);
      load();
    } catch (e) { toast.error(errText(e, "Couldn't update week")); }
  }

  async function deleteWeek(c, w) {
    if (!window.confirm(`Remove the week ${w.n} report for ${c.first_name} ${c.last_name}? This can't be undone.`)) return;
    try {
      await rapi.delete(`/referrals/admin/clients/${c.id}/weeks/${w.n}`);
      toast.success(`Week ${w.n} report removed`);
      load();
    } catch (e) { toast.error(errText(e, "Couldn't remove week")); }
  }

  function selectForFax(c) {
    setFaxClient(c.code);
    document.querySelector("[data-testid='referrals-client-fax']")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function resendFax() {
    if (!retryNumber.trim()) { toast.error("Enter the fax number"); return; }
    try {
      const { data } = await rapi.post(`/referrals/admin/fax/${retryFax.id}/retry`, { number: retryNumber });
      if (data.ok) toast.success(`Fax re-queued to ${data.to_number}`);
      else toast.error(data.detail || "Couldn't re-send the fax");
      setRetryFax(null);
      load();
    } catch (e) { toast.error(errText(e, "Couldn't re-send the fax")); }
  }

  async function optOutPhysician(p) {
    if (!window.confirm(`Add ${p.name} (${p.fax}) to the do-not-contact list? They will no longer receive program faxes.`)) return;
    try {
      await rapi.post("/referrals/admin/fax-optout", { number: p.fax_raw || p.fax, name: p.name });
      toast.success(`${p.name} added to do-not-contact`);
      load();
    } catch (e) { toast.error(errText(e, "Couldn't update the list")); }
  }

  async function restoreOptOut(o) {
    try {
      await rapi.delete(`/referrals/admin/fax-optout/${o.fax_raw}`);
      toast.success(`${o.fax} restored — can receive faxes again`);
      load();
    } catch (e) { toast.error(errText(e, "Couldn't restore")); }
  }

  // ── physician list filing: Added / Removed / Active / Inactive toggles ──
  async function patchPhysician(p, updates, msg) {
    try {
      await rapi.patch(`/referrals/admin/physicians/${p.id}`, updates);
      if (msg) toast.success(msg);
      load();
    } catch (e) { toast.error(errText(e, "Couldn't update the physician")); }
  }
  async function patchClinic(p, updates, msg) {
    try {
      await rapi.patch(`/referrals/admin/clinics/${p.id}`, updates);
      if (msg) toast.success(msg);
      load();
    } catch (e) { toast.error(errText(e, "Couldn't update the clinic")); }
  }
  // Delete on any list tab: never erases — files the record under Removed
  // (one tap + toast, same as every other tab toggle)
  function fileRemoved(p) {
    if (p.source === "clinic") patchClinic(p, { removed: true }, `${p.name || p.clinic} filed under Removed`);
    else patchPhysician(p, { removed: true }, `${p.name || p.clinic} filed under Removed`);
  }
  // Removed tab → back: manual physicians return to Added, clinics to Active
  // (one tap + toast — same as the Active ⇄ Inactive toggles)
  function restoreRemoved(p) {
    if (p.source === "clinic") patchClinic(p, { removed: false, inactive: false }, `${p.name} restored to Active`);
    else patchPhysician(p, { removed: false, added: true }, `${p.name} restored to Added`);
  }
  // Delete on No contact: unblock the number + file the record under Removed
  async function deleteOptOutRecord(p) {
    const note = p.source === "optout"
      ? "The number can receive faxes again if it's ever re-added."
      : "The record will be filed under Removed.";
    if (!window.confirm(`Delete ${p.name || p.fax} from the no-contact list? ${note}`)) return;
    try {
      await rapi.delete(`/referrals/admin/fax-optout/${p.fax_raw}`);
      if (p.source === "manual") await rapi.patch(`/referrals/admin/physicians/${p.id}`, { removed: true });
      else if (p.source === "clinic") await rapi.patch(`/referrals/admin/clinics/${p.id}`, { removed: true });
      toast.success("Deleted from no-contact");
      load();
    } catch (e) { toast.error(errText(e, "Couldn't delete")); }
  }

  // ── keep the viewport pinned when toggles change list height ──
  // Anchors the toggle row itself to its viewport position, so tapping a
  // filter/tab never visibly moves the page.
  const keepScroll = (anchorSel) => (fn) => (...args) => {
    const el = document.querySelector(anchorSel);
    const top = el ? el.getBoundingClientRect().top : 0;
    fn(...args);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (el) window.scrollBy(0, el.getBoundingClientRect().top - top);
    }));
  };

  // ── row edit: pre-fill the top form (clinics and manual physicians) ──
  function startRowEdit(p) {
    const addr = splitAddress(p.address || "");
    setNewPhys({
      name: p.name || "", clinic: p.clinic || "",
      fax: p.fax || "", phone: p.phone || "",
      email: p.email || "", cpso: p.cpso || "",
      street: addr.street, city: addr.city, province: addr.province, postal: addr.postal,
      promo_rate: p.promo_rate === 100 ? 100 : 75,
      specialty: p.specialty || "",
    });
    setEditingRow({ source: p.source, id: p.id, code: p.code_display, name: p.name });
    setPhysMiss({});
    setPhysFaxConflict(null);
    document.querySelector("[data-testid='phys-name']")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  function cancelRowEdit() {
    setEditingRow(null);
    setNewPhys({ ...EMPTY_PHYS });
    setPhysMiss({});
    setPhysFaxConflict(null);
  }

  // ── CPSO register paste helpers ──
  function applyParsed(kind, parsed, clearFallback) {
    if (kind === "name") {
      if (!parsed.name && !parsed.cpso) return false;
      setNewPhys((p) => ({ ...p, ...parsed }));
      toast.success(`Parsed — ${[parsed.name, parsed.cpso && `CPSO ${parsed.cpso}`].filter(Boolean).join(" · ")}`);
    } else {
      const got = [parsed.street, parsed.city, parsed.province, parsed.postal, parsed.phone, parsed.fax, parsed.email].filter(Boolean);
      if (!got.length) return false;
      setNewPhys((p) => ({ ...p, ...parsed }));
      toast.success(`Parsed — ${got.join(" · ")}`);
    }
    if (clearFallback) setPasteFallback(null);
    return true;
  }

  async function pasteFromClipboard(kind) {
    let t = "";
    try {
      t = await navigator.clipboard.readText();
    } catch {
      setPasteFallback(kind);  // browser blocked clipboard — show a paste box instead
      return;
    }
    if (!t.trim()) { toast.error("Clipboard is empty — copy from the college site first"); return; }
    const parsed = kind === "name" ? parseNameBlock(t) : parseAddrBlock(t);
    if (!applyParsed(kind, parsed, false)) {
      toast.error(kind === "name"
        ? "Couldn't find a name + CPSO# in the clipboard — copy the physician's name block"
        : "Couldn't find address / phone / fax in the clipboard — copy the practice block");
    }
  }

  function onFallbackPaste(e) {
    const t = e.target.value;
    const parsed = pasteFallback === "name" ? parseNameBlock(t) : parseAddrBlock(t);
    applyParsed(pasteFallback, parsed, true);
  }

  const isLatestWeek = (c, w) => Math.max(0, ...(c.weeks || []).map((x) => x.n)) === w.n;

  const editInp = { ...inp, flex: "1 1 45%", minWidth: 140 };

  function renderEditDialog() {
    return (
      <div style={{ ...rec, background: "#FAFAF7" }} data-testid="edit-dialog">
        <b style={{ fontSize: 13 }}>Edit — {editing.first_name} {editing.last_name}</b>
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <input style={editInp} value={editing.first_name} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} placeholder="First name" data-testid="edit-first-name" />
          <input style={editInp} value={editing.last_name} onChange={(e) => setEditing({ ...editing, last_name: e.target.value })} placeholder="Last name" data-testid="edit-last-name" />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <input type="date" style={{ ...editInp, minWidth: 150 }} value={editing.dob} onChange={(e) => setEditing({ ...editing, dob: e.target.value })} data-testid="edit-dob" />
          <input style={editInp} value={editing.health_card} onChange={(e) => setEditing({ ...editing, health_card: e.target.value.toUpperCase() })} placeholder="Health card" data-testid="edit-health-card" />
          <input style={{ ...editInp, flex: "1 1 30%", minWidth: 90 }} value={editing.total_weeks || ""} onChange={(e) => setEditing({ ...editing, total_weeks: e.target.value.replace(/\D/g, "").slice(0, 2) })} placeholder="Weeks" data-testid="edit-weeks" />
        </div>
        <div style={acts}>
          <button style={{ ...abtn, background: GREEN, color: "#fff", border: `1px solid ${GREEN}` }} onClick={saveEdit} data-testid="edit-save">Save</button>
          <button style={abtn} onClick={() => setEditing(null)} data-testid="edit-cancel">Cancel</button>
        </div>
      </div>
    );
  }

  // Week pay/edit dialogs render INLINE, directly under the record they edit
  // (a long list with the dialog at the bottom is unusable on mobile).
  function renderPayDialog() {
    return (
      <div style={{ ...rec, background: "#FAFAF7" }} data-testid="pay-dialog">
        <b style={{ fontSize: 13 }}>Mark paid — {payWeek.client.first_name} {payWeek.client.last_name}, Week {payWeek.week.n} ({money(75)})</b>
        <div style={{ ...acts, alignItems: "center" }}>
          <DatePickerButton value={payDate} onChange={setPayDate} testId="pay-date" />
          <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} data-testid="pay-method"
            style={{ ...abtn, flex: "none", minWidth: 110, height: 30 }}>
            <option value="eft">E-transfer</option>
            <option value="cash">Cash</option>
          </select>
          <button style={{ ...abtn, background: GREEN, color: "#fff", border: `1px solid ${GREEN}` }} onClick={markPaid} data-testid="pay-confirm">Confirm paid</button>
          <button style={abtn} onClick={() => setPayWeek(null)}>Cancel</button>
        </div>
      </div>
    );
  }

  function renderWeekEditDialog() {
    return (
      <div style={{ ...rec, background: "#FAFAF7" }} data-testid="week-edit-dialog">
        <b style={{ fontSize: 13 }}>Edit — {editWeek.client.first_name} {editWeek.client.last_name}, Week {editWeek.week.n} ({money(75)})</b>
        <div style={{ ...acts, alignItems: "center", flexWrap: "wrap" }}>
          <select value={ewStatus} onChange={(e) => setEwStatus(e.target.value)} data-testid="week-edit-status"
            style={{ ...abtn, flex: "none", minWidth: 110, height: 30 }}>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="paid">Paid</option>
          </select>
          {ewStatus === "paid" && (
            <>
              <DatePickerButton value={ewDate} onChange={setEwDate} testId="week-edit-date" />
              <select value={ewMethod} onChange={(e) => setEwMethod(e.target.value)} data-testid="week-edit-method"
                style={{ ...abtn, flex: "none", minWidth: 110, height: 30 }}>
                <option value="eft">E-transfer</option>
                <option value="cash">Cash</option>
              </select>
            </>
          )}
          <button style={{ ...abtn, background: GREEN, color: "#fff", border: `1px solid ${GREEN}` }} onClick={saveWeekEdit} data-testid="week-edit-save">Save</button>
          <button style={abtn} onClick={() => setEditWeek(null)} data-testid="week-edit-cancel">Cancel</button>
        </div>
      </div>
    );
  }

  // when an inline dialog opens under its record, make sure it's on screen
  // (keyed by record id so typing in the form doesn't re-scroll)
  const dialogKey = editWeek ? `we-${editWeek.client.id}-${editWeek.week.n}`
    : payWeek ? `pay-${payWeek.client.id}-${payWeek.week.n}`
    : editing ? `cl-${editing.id}` : "";
  useEffect(() => {
    if (!dialogKey) return;
    const el = document.querySelector("[data-testid='week-edit-dialog'], [data-testid='pay-dialog'], [data-testid='edit-dialog']");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [dialogKey]);

  // ── fax ──
  async function openPdf(resp) {
    const blob = new Blob([resp.data], { type: "application/pdf" });
    window.open(URL.createObjectURL(blob), "_blank");
  }

  async function previewFax(template, clientCode, clinicCode, rate) {
    faxStart("preview");
    try {
      const resp = await rapi.post("/referrals/admin/fax/preview",
        { template, client_code: clientCode || undefined, clinic_code: clinicCode || undefined, rate: rate || undefined },
        { responseType: "blob" });
      await openPdf(resp);
    } catch (e) { toast.error("Couldn't render the preview"); }
    finally { faxDone(); }
  }

  async function downloadPromoPdf(rate) {
    faxStart("download");
    try {
      const resp = await rapi.post(`/referrals/admin/promo/download?rate=${rate || 75}`, {}, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `HepCURE-Program-Promo-$${rate || 75}.pdf`;
      a.target = "_blank";   // iOS: open in a new tab (like Preview) instead of navigating away
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Colour promo PDF downloaded — ready to email");
    } catch (e) { toast.error("Couldn't download the PDF"); }
    finally { faxDone(); }
  }

  function faxGuard(data) {
    if (data && data.configured === false) {
      toast.error("SRFax is not configured yet — faxing activates when the credentials are added.");
      return true;
    }
    return false;
  }

  async function sendTestFax(template, number, clientCode, clinicCode, rate) {
    if (!number.trim()) { toast.error("Enter the test fax number first"); return; }
    faxStart("test");
    try {
      const { data } = await rapi.post("/referrals/admin/fax/test",
        { template, number, client_code: clientCode || undefined, clinic_code: clinicCode || undefined, rate: rate || undefined });
      if (faxGuard(data)) return;
      if (data.ok) toast.success(`Test fax queued to ${data.to_number}`);
      else toast.error(data.detail || "Fax failed to queue");
      load();
    } catch (e) { toast.error(errText(e, "Couldn't send test fax")); }
    finally { faxDone(); }
  }

  async function sendFax(template, clinicCode, clientCode) {
    faxStart("send");
    try {
      const { data } = await rapi.post("/referrals/admin/fax/send",
        { template, clinic_code: clinicCode, client_code: clientCode || undefined });
      if (faxGuard(data)) return;
      if (data.ok) toast.success(`Fax queued to ${data.to_number}`);
      else toast.error(data.detail || "Fax failed to queue");
      load();
    } catch (e) { toast.error(errText(e, "Couldn't send fax")); }
    finally { faxDone(); }
  }

  async function broadcast() {
    const ruleNote = faxSuppression === "none"
      ? "everyone is re-faxed, even recently-sent numbers"
      : faxSuppression === "all"
        ? "every number ever faxed is skipped"
        : `numbers faxed in the last ${faxDaysNum} days are skipped`;
    if (!window.confirm(`Fax the program promo to all ${promotingPhys.length} physicians? Added or removed physicians, shared fax numbers, and no-contact numbers are skipped — and ${ruleNote}.`)) return;
    faxStart("broadcast");
    try {
      const { data } = await rapi.post("/referrals/admin/fax/broadcast",
        { suppression: faxSuppression, days: faxDaysNum });
      if (faxGuard(data)) return;
      toast.success(`Broadcast done — ${data.sent} sent, ${data.skipped_recent} already sent, ${data.skipped_joined || 0} added, ${data.skipped_duplicate || 0} shared fax, ${data.skipped_optout || 0} no contact, ${data.failed} failed`);
      load();
    } catch (e) { toast.error(errText(e, "Broadcast failed")); }
    finally { faxDone(); }
  }

  // ── physicians ──
  // Specialty picker options (shared list; deleting an option never touches
  // physicians who already carry it)
  async function addSpecialty() {
    const name = specNew.trim();
    if (!name) return;
    try {
      const { data } = await rapi.post("/referrals/admin/specialties", { name });
      setSpecialties(data.specialties || []);
      setSpecNew("");
      setNewPhys((np) => ({ ...np, specialty: name }));  // adding picks it for this clinician
    } catch (e) { toast.error(errText(e, "Couldn't add specialty")); }
  }
  async function deleteSpecialty(name) {
    try {
      const { data } = await rapi.delete(`/referrals/admin/specialties/${encodeURIComponent(name)}`);
      setSpecialties(data.specialties || []);
      if (newPhys.specialty === name) setNewPhys((np) => ({ ...np, specialty: "" }));
      if (specFilter === name) setSpecFilter("");
    } catch (e) { toast.error(errText(e, "Couldn't delete specialty")); }
  }

  async function addPhysician(allowDup = false) {
    // red-highlight the required fields instead of a bare toast (same system
    // as the public form — highlight clears as soon as the field is filled)
    const miss = { name: !newPhys.name.trim(), fax: !newPhys.fax.trim() };
    setPhysMiss(miss);
    if (miss.name || miss.fax) { toast.error("Complete the highlighted fields"); return; }
    if (cpsoDup) { toast.error(`Licence number already registered to ${cpsoDup.name}`); return; }
    setPhysFaxConflict(null);
    try {
      // same join as the public /referrals form: "street, city province postal"
      const { street, city, province, postal, ...rest } = newPhys;
      const cityProv = [city.trim(), province.trim(), postal.trim()].filter(Boolean).join(" ");
      const address = [street.trim(), cityProv].filter(Boolean).join(", ");
      if (editingRow) {
        if (editingRow.source === "clinic") {
          await rapi.put(`/referrals/clinic/${editingRow.code}`, {
            physician_name: rest.name, clinic_name: rest.clinic, fax: rest.fax,
            phone: rest.phone, email: rest.email, address, cpso: rest.cpso,
          });
        } else {
          await rapi.patch(`/referrals/admin/physicians/${editingRow.id}`, { ...rest, address });
        }
        toast.success("Physician updated");
        setEditingRow(null);
      } else {
        try {
          await rapi.post("/referrals/admin/physicians", { ...rest, address, allow_duplicate_fax: allowDup });
        } catch (err) {
          // duplicate fax → show the conflict dialog (Add as new record / Cancel)
          const det = err?.response?.data?.detail;
          if (err?.response?.status === 409 && det?.error === "fax_exists") {
            setPhysFaxConflict(det.existing);
            setTimeout(() => document.querySelector('[data-testid="phys-fax-conflict"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
            return;
          }
          throw err;
        }
        toast.success("Physician added");
      }
      setNewPhys({ ...EMPTY_PHYS });
      setPhysMiss({});
      setPhysFaxConflict(null);
      load();
    } catch (e) { toast.error(errText(e, editingRow ? "Couldn't update" : "Couldn't add physician")); }
  }

  // Clear the whole physician form — fields, edit mode, duplicate-fax notice
  // and validation highlights — without saving anything.
  function cancelPhysicianForm() {
    setNewPhys({ ...EMPTY_PHYS });
    setEditingRow(null);
    setPhysMiss({});
    setPhysFaxConflict(null);
  }

  async function importCsv(ev) {
    const file = ev.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    try {
      const { data } = await rapi.post("/referrals/admin/physicians/import-csv", { csv_text: text });
      toast.success(`Imported ${data.added} physician${data.added === 1 ? "" : "s"}${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      load();
    } catch (e) { toast.error(errText(e, "Import failed")); }
    ev.target.value = "";
  }

  async function deletePhysician(p) {
    if (!window.confirm(`Delete ${p.name} forever? This cannot be undone.`)) return;
    try {
      await rapi.delete(`/referrals/admin/physicians/${p.id}`);
      load();
    } catch (e) { toast.error(errText(e, "Only manually-added physicians can be removed")); }
  }

  async function deleteClinic(p) {
    if (!window.confirm(`Delete ${p.clinic} (${p.code_display}) forever? Their clinic code will stop working. This cannot be undone.`)) return;
    try {
      await rapi.delete(`/referrals/admin/clinics/${p.id}`);
      toast.success("Clinic removed");
      load();
    } catch (e) { toast.error(errText(e, "Couldn't remove clinic")); }
  }

  async function deleteFax(f) {
    if (!window.confirm(`Delete this ${f.template} fax log to ${f.to_number}?`)) return;
    try {
      await rapi.delete(`/referrals/admin/faxes/${f.id}`);
      toast.success("Fax log deleted");
      load();
    } catch (e) { toast.error(errText(e, "Couldn't delete fax log")); }
  }

  // ── physician fax list: one table shape, per-tab action buttons ──
  // Select pins a clinician to the promo section below — Preview / Download /
  // test fax then render that clinician's rate.
  const [promoPhysId, setPromoPhysId] = useState(null);
  const promoPhysObj = useMemo(
    () => physicians.find((p) => p.id === promoPhysId) || null,
    [physicians, promoPhysId]
  );
  const editBtn = (p, tabKey) => (
    <button className="ref-mini" onClick={() => startRowEdit(p)} data-testid={`${tabKey}-edit-${p.id}`}>✎ Edit</button>
  );
  const promoSelectBtn = (p, tabKey) => (
    <button className={`ref-mini ${promoPhysId === p.id ? "solid" : "navy"}`}
      onClick={() => {
        const next = promoPhysId === p.id ? null : p.id;
        setPromoPhysId(next);
        if (next) document.querySelector("[data-testid='promo-preview']")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }}
      data-testid={`${tabKey}-select-${p.id}`}>{promoPhysId === p.id ? "✓ Selected" : "Select"}</button>
  );
  const deleteBtn = (p, tabKey) => (   // files under Removed — never erases
    <button className="ref-mini" style={{ color: RED }} onClick={() => fileRemoved(p)} data-testid={`${tabKey}-delete-${p.id}`}>Delete</button>
  );
  const hardDeleteBtn = (p, tabKey) => (   // Removed tab only — erases for good
    <button className="ref-mini" style={{ color: RED }} onClick={() => (p.source === "manual" ? deletePhysician(p) : deleteClinic(p))} data-testid={`${tabKey}-delete-${p.id}`}>Delete</button>
  );
  const promotingActions = (p) => (<>
    <button className="ref-mini" style={{ color: SUCCESS, borderColor: SUCCESS }}
      onClick={() => patchPhysician(p, { added: true }, `${p.name} marked Added — off the fax queue`)}
      data-testid={`promoting-add-${p.id}`}>Added</button>{" "}
    <button className="ref-mini" style={{ color: RED }} onClick={() => optOutPhysician(p)}
      data-testid={`promoting-optout-${p.id}`}>Do not fax</button>{" "}
    {promoSelectBtn(p, "promoting")}{" "}
    {editBtn(p, "promoting")}{" "}{deleteBtn(p, "promoting")}
  </>);
  const addedActions = (p) => (<>
    <button className="ref-mini" style={{ color: MUTED, borderColor: MUTED }} onClick={() => fileRemoved(p)}
      data-testid={`added-removed-${p.id}`}>Removed</button>{" "}
    {promoSelectBtn(p, "added")}{" "}
    {editBtn(p, "added")}{" "}{deleteBtn(p, "added")}
  </>);
  const removedActions = (p) => (<>
    <button className="ref-mini" style={{ color: SUCCESS, borderColor: SUCCESS }} onClick={() => restoreRemoved(p)}
      data-testid={`removed-add-${p.id}`}>{p.source === "clinic" ? "Active" : "Added"}</button>{" "}
    {editBtn(p, "removed")}{" "}{hardDeleteBtn(p, "removed")}
  </>);
  const activeActions = (p) => (<>
    <button className="ref-mini" style={{ color: MUTED, borderColor: MUTED }}
      onClick={() => patchClinic(p, { inactive: true }, `${p.name} marked Inactive`)}
      data-testid={`active-inactive-${p.id}`}>Inactive</button>{" "}
    {editBtn(p, "active")}{" "}{deleteBtn(p, "active")}
  </>);
  const inactiveActions = (p) => (<>
    <button className="ref-mini" style={{ color: SUCCESS, borderColor: SUCCESS }}
      onClick={() => patchClinic(p, { inactive: false }, `${p.name} back to Active`)}
      data-testid={`inactive-active-${p.id}`}>Active</button>{" "}
    {editBtn(p, "inactive")}{" "}{deleteBtn(p, "inactive")}
  </>);
  const optoutActions = (p) => (<>
    <button className="ref-mini navy" onClick={() => restoreOptOut(p)}
      data-testid={`optout-restore-${p.fax_raw || p.id}`}>{p.source === "clinic" ? "Active" : "Promoting"}</button>{" "}
    {p.source !== "optout" && <>{editBtn(p, "optout")}{" "}</>}
    <button className="ref-mini" style={{ color: RED }} onClick={() => deleteOptOutRecord(p)}
      data-testid={`optout-delete-${p.fax_raw || p.id}`}>Delete</button>
  </>);

  const physTable = (rows, tabKey, emptyText, renderActions) => (
    <div ref={physTableRef} style={{ overflowX: "auto", maxHeight: 460, overflowY: "auto", scrollbarWidth: "thin", overscrollBehavior: "none", minHeight: Math.min(physTableMinH, 460) || undefined }} data-testid={`${tabKey}-list`}>
      <table className="ref-phys-table" data-testid={`${tabKey}-table`}>
        <thead>
          <tr><th>Clinician</th><th>Clinic</th><th>Phone</th><th>Fax</th><th>Last faxed</th><th></th></tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={`${p.source}-${p.id}`} data-testid={`${tabKey}-row-${p.id}`}>
              <td>{p.specialty ? <div><span style={{ display: "inline-block", marginLeft: -7, padding: "1px 7px", borderRadius: 999, background: "rgba(31,78,121,.08)", color: NAVY, fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap" }} data-testid={`${tabKey}-spec-${p.id}`}>{p.specialty}</span></div> : null}<b>{shortName(p.name) || "—"}</b>{p.code_display ? <span style={{ fontFamily: "ui-monospace, monospace", color: NAVY, fontSize: 11, marginLeft: 6 }}>{p.code_display}</span> : null}</td>
              <td>{p.clinic || "—"}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                {p.phone ? (
                  <a href={`tel:+1${String(p.phone).replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "")}`}
                    style={{ color: NAVY, fontWeight: 600, textDecoration: "none" }}
                    data-testid={`${tabKey}-call-${p.id}`}>{p.phone}</a>
                ) : "—"}
              </td>
              <td style={{ whiteSpace: "nowrap" }}>{p.fax}</td>
              <td style={{ fontSize: 11.5, color: MUTED }}>{p.last_faxed_at ? fmtDate(p.last_faxed_at) : "never"}</td>
              <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>{renderActions(p)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={6} style={{ color: MUTED, fontSize: 12 }} data-testid={`no-${tabKey}`}>{emptyText}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  // ═══════════════════════ render ═══════════════════════
  return (
    <div data-testid="referrals-panel" style={{
      background: "#fff", borderRadius: 14, border: `1px solid ${BORDER}`, padding: 20,
    }}>
      {/* Payout summary — same 4 quadrants as the incentive teams. The Day +
          Month selectors that drive them live with the fax list below
          (Fax confirmations group); cards always label their period. */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        gap: 10, marginBottom: 14,
      }} data-testid="referrals-totals">
        <TotalCard testId="referrals-total-records"
          label="Records" value={paySummary?.records || 0}
          sub={day === "all" ? "This month" : "Selected day"} />
        <TotalCard testId="referrals-total-cash"
          label="Cash" value={money(paySummary?.cash_total || 0)}
          sub={`${paySummary?.cash_count || 0} record${(paySummary?.cash_count || 0) === 1 ? "" : "s"}`} />
        <TotalCard testId="referrals-total-eft"
          label="EFT" value={money(paySummary?.eft_total || 0)}
          sub={`${paySummary?.eft_count || 0} record${(paySummary?.eft_count || 0) === 1 ? "" : "s"}`} />
        <TotalCard testId="referrals-total-paid"
          label="Total paid" value={money(paySummary?.total || 0)}
          sub={`Referrals · ${monthLabel}`} strong />
      </div>
      <style>{`
        .inc-team-month {
          display: flex; align-items: stretch; border: 1px solid #e2e8f0;
          background: #fff; border-radius: 6px; overflow: hidden; width: 100%;
        }
        .inc-day-select, .inc-month-select {
          flex: 1; height: 36px; padding: 0 30px 0 12px; font-size: 10.5px; font-weight: 700;
          color: #fff; background: ${NAVY}; border: 0; text-align: center;
          -webkit-appearance: none; appearance: none;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='white'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.38a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z' clip-rule='evenodd'/></svg>");
          background-repeat: no-repeat; background-position: right 8px center;
          cursor: pointer;
        }
        .inc-day-select { border-right: 1px solid rgba(255,255,255,0.20); }
        .ref-phys-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
        .ref-phys-table th { text-align: left; font-size: 10px; font-weight: 700; color: ${MUTED};
          text-transform: uppercase; letter-spacing: .12em; padding: 6px 8px; border-bottom: 1px solid ${BORDER}; white-space: nowrap;
          position: sticky; top: 0; background: #fff; z-index: 2; }
        .ref-phys-table td { padding: 8px; border-bottom: 1px solid ${BORDER}; vertical-align: middle; white-space: nowrap; }
        .ref-phys-table { background: #fff; }
        .ref-mini { border-radius: 6px; padding: 4px 8px; font-size: 10.5px; font-weight: 700;
          border: 1px solid ${BORDER}; background: #fff; color: ${LABEL}; cursor: pointer; white-space: nowrap; }
        .ref-mini.navy { border-color: ${NAVY}; color: ${NAVY}; }
        .ref-mini.solid { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
        /* row action buttons share one width so every row lines up */
        .ref-phys-table td:last-child .ref-mini { min-width: 78px; text-align: center; }
        .ref-spin { animation: ref-spin .9s linear infinite; }
        @keyframes ref-spin { to { transform: rotate(360deg); } }
        /* fax meta: date · Ref # share one line on desktop; on mobile the
           Ref # wraps to its own line and the separator dot disappears */
        .ref-fax-ref { display: inline; }
        @media (max-width: 640px) {
          .ref-fax-ref { display: block; }
          .ref-fax-sep { display: none; }
        }
        .ref-radio { display: flex; gap: 10px; align-items: flex-start; padding: 10px 13px;
          border-bottom: 1px solid #F1F0EC; cursor: pointer; }
        .ref-radio:last-child { border-bottom: 0; }
      `}</style>

      {faxConfigured === false && (
        <div style={{
          background: "rgba(229,138,46,.08)", border: "1px solid rgba(229,138,46,.4)",
          borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#8A5A00", marginBottom: 4,
        }} data-testid="fax-standby-banner">
          <b>Fax delivery is in standby.</b> Everything works — previews, lists, queues — and real
          faxing activates the moment the SRFax credentials are added. Use "Send test fax" to your
          own fax line once they are.
        </div>
      )}

      {/* ══ GROUP 1 · patients ══ */}
      <Group
        title="Patients"
        sub="Registrations, reminders and client faxes."
        testId="referrals-group-patients"
      >
      <input
        style={{ ...searchInp, marginTop: 8, marginBottom: 12 }}
        placeholder="Search clients by name, code or clinic…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        data-testid="referrals-search"
      />

      {/* ── 1 · new registrations ── */}
      <Section
        title="New registrations — under review"
        testId="referrals-registrations"
        flush
      >
        {pendingRegs.length === 0 && (
          <div style={{ fontSize: 12, color: MUTED }} data-testid="no-registrations">No registrations waiting for review.</div>
        )}
        {pendingRegs.map((c) => (
          <Fragment key={c.id}>
          <div style={{ ...rec, borderColor: "rgba(31,78,121,.45)" }} data-testid={`reg-${c.id}`}>
            <b style={{ fontSize: 13 }}>{c.first_name} {c.last_name}</b>
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>{c.clinic?.clinic_name}</div>
            {c.office_name && c.office_name !== c.clinic?.clinic_name && (
              <div style={{ fontSize: 11.5, color: MUTED, marginTop: 1 }} data-testid={`reg-office-${c.id}`}>{c.office_name}</div>
            )}
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>Submitted {fmtDate(c.created_at)}</div>
            <div style={{ fontSize: 11.5, marginTop: 2, fontFamily: "ui-monospace, monospace", color: NAVY, fontWeight: 700 }}>{c.code_display}</div>
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>DOB {c.dob}</div>
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }} data-testid={`reg-hc-${c.id}`}>Health card {c.health_card}</div>
            <div style={{ ...acts, flexWrap: "wrap" }}>
              {approvingId === c.id ? renderApproveStep(c) : (<>
                <button className="ref-mini" style={{ flex: 1, background: GREEN, color: "#fff", border: `1px solid ${GREEN}` }}
                  onClick={() => setApprovingId(c.id)} data-testid={`approve-${c.id}`}>✓ Approve</button>
                <button className="ref-mini" style={{ flex: 1, color: "#9A6208", border: `1px solid ${AMBER}` }}
                  onClick={() => pendRegistration(c)} data-testid={`pend-${c.id}`}>Pending</button>
                <button className="ref-mini" style={{ flex: 1, color: RED, border: `1px solid ${RED}` }}
                  onClick={() => denyRegistration(c)} data-testid={`deny-${c.id}`}>✕ Deny</button>
                <button className="ref-mini" style={{ flex: 1 }} onClick={() => setEditing({ ...c })} data-testid={`edit-${c.id}`}>✎ Edit</button>
                <button className="ref-mini" style={{ flex: 1, color: RED }} onClick={() => deleteClient(c)} data-testid={`delete-${c.id}`}>🗑</button>
              </>)}
              <button
                className={`ref-mini ${faxClient === c.code ? "solid" : ""}`}
                style={{ flex: "1 1 100%", justifyContent: "center" }}
                onClick={() => selectForFax(c)}
                data-testid={`reg-select-${c.id}`}
              >{faxClient === c.code ? "✓ Selected — ready to fax below" : "Select for fax (blood work requisition, updates…)"}</button>
            </div>
            {weeksErr[c.id] && (
              <div style={{ color: RED, fontSize: 11, fontWeight: 600, marginTop: 6 }} data-testid={`weeks-error-${c.id}`}>
                {weeksErr[c.id]}
              </div>
            )}
          </div>
          {/* edit dialog opens right under THIS registration */}
          {editing && editing.status !== "approved" && editing.id === c.id && renderEditDialog()}
          </Fragment>
        ))}
      </Section>

      {/* ── 2 · weekly reminder reports ── */}
      <Section
        title={pendingWeeksCount ? `Weekly reminder reports — ${pendingWeeksCount} pending` : "Weekly reminder reports"}
        testId="referrals-weeks"
      >
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          <button
            style={{ ...abtn, flex: 1, height: 34, justifyContent: "center", ...(weeksTab === "open" ? { background: NAVY, color: "#fff", border: `1px solid ${NAVY}` } : {}) }}
            onClick={keepScroll("[data-testid='weeks-tab-open']")(() => setWeeksTab("open"))} data-testid="weeks-tab-open">Pending ({openWeeks.length})</button>
          <button
            style={{ ...abtn, flex: 1, height: 34, justifyContent: "center", ...(weeksTab === "paid" ? { background: SUCCESS, color: "#fff", border: `1px solid ${SUCCESS}` } : {}) }}
            onClick={keepScroll("[data-testid='weeks-tab-open']")(() => setWeeksTab("paid"))} data-testid="weeks-tab-paid">Paid ({paidWeeks.length})</button>
        </div>
        {visibleWeeks.length === 0 && (
          <div style={{ fontSize: 12, color: MUTED }} data-testid="no-weeks">
            {weeksTab === "paid" ? "No paid reports yet." : "No weekly reports waiting."}
          </div>
        )}
        {visibleWeeks.map(({ client: c, week: w }) => {
          const st = WEEK_STATUS[w.status] || WEEK_STATUS.pending;
          return (
            <Fragment key={`${c.id}-${w.n}`}>
            <div style={rec} data-testid={`week-row-${c.id}-${w.n}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <b style={{ fontSize: 13 }}>{c.first_name} {c.last_name}</b>
                <span
                  style={{
                    flexShrink: 0, fontSize: 10, fontWeight: 800, textTransform: "uppercase",
                    letterSpacing: ".08em", color: st.color, border: `1.5px solid ${st.color}`,
                    borderRadius: 99, padding: "2px 8px",
                  }}
                  data-testid={`week-status-${c.id}-${w.n}`}
                >{st.label}</span>
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: LABEL, marginTop: 2 }}>Week {w.n}</div>
              <div style={{ fontSize: 11.5, color: MUTED, marginTop: 1 }}>{c.clinic?.clinic_name}{c.office_name && c.office_name !== c.clinic?.clinic_name ? ` — ${c.office_name}` : ""}</div>
              {w.status !== "paid" && c.pay_email && (
                <div style={{ fontSize: 11.5, color: MUTED, marginTop: 1 }} data-testid={`week-payemail-${c.id}-${w.n}`}>E-transfer to {c.pay_email}</div>
              )}
              <div style={{ fontSize: 11.5, color: MUTED, marginTop: 1 }}>
                Submitted {fmtDate(w.submitted_at)} · {money(75)}
              </div>
              {w.status === "paid" && (
                <div style={{ fontSize: 11.5, color: MUTED, marginTop: 1 }}>
                  Paid {fmtDate(w.payment_date)} · {w.payment_method === "cash" ? "Cash" : "E-transfer"}
                </div>
              )}
              <div style={acts}>
                {w.status === "pending" && (
                  <button style={{ ...abtn, background: GREEN, color: "#fff", border: `1px solid ${GREEN}` }}
                    onClick={() => approveWeek(c, w)} data-testid={`week-approve-${c.id}-${w.n}`}>✓ Approve & pay</button>
                )}
                {w.status === "approved" && (
                  <button style={{ ...abtn, background: NAVY, color: "#fff", border: `1px solid ${NAVY}` }}
                    onClick={() => { setPayWeek({ client: c, week: w }); setPayDate(toYMD(new Date())); setPayMethod("eft"); }}
                    data-testid={`week-pay-${c.id}-${w.n}`}>$ Mark paid</button>
                )}
                <button style={abtn} onClick={() => openWeekEdit(c, w)} data-testid={`week-edit-${c.id}-${w.n}`}>✎ Edit</button>
                {isLatestWeek(c, w) && (
                  <button style={{ ...abtn, color: RED }} onClick={() => deleteWeek(c, w)}
                    data-testid={`week-delete-${c.id}-${w.n}`}>🗑</button>
                )}
              </div>
            </div>
            {/* pay / edit dialogs open right under THIS record */}
            {payWeek && payWeek.client.id === c.id && payWeek.week.n === w.n && renderPayDialog()}
            {editWeek && editWeek.client.id === c.id && editWeek.week.n === w.n && renderWeekEditDialog()}
            </Fragment>
          );
        })}

      </Section>

      {/* ── 2.5 · qualified clients ── */}
      <Section title="Clients" testId="referrals-qualified">
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          <button
            style={{ ...abtn, flex: 1, height: 34, justifyContent: "center", ...(clientsTab === "qualified" ? { background: NAVY, color: "#fff", border: `1px solid ${NAVY}` } : {}) }}
            onClick={() => setClientsTab("qualified")} data-testid="clients-tab-qualified">Qualified ({approvedClients.length})</button>
          <button
            style={{ ...abtn, flex: 1, height: 34, justifyContent: "center", ...(clientsTab === "pending" ? { background: AMBER, color: "#fff", border: `1px solid ${AMBER}` } : {}) }}
            onClick={() => setClientsTab("pending")} data-testid="clients-tab-pending">Pending ({pendingClients.length})</button>
          <button
            style={{ ...abtn, flex: 1, height: 34, justifyContent: "center", ...(clientsTab === "denied" ? { background: RED, color: "#fff", border: `1px solid ${RED}` } : {}) }}
            onClick={() => setClientsTab("denied")} data-testid="clients-tab-denied">Denied ({deniedClients.length})</button>
        </div>
        {clientsTab === "qualified" && approvedClients.length === 0 && (
          <div style={{ fontSize: 12, color: MUTED }} data-testid="no-qualified">No qualified clients yet.</div>
        )}
        {clientsTab === "qualified" && approvedClients.length > 0 && (
          <div data-testid="approved-clients-summary" style={listScroll}>
            {approvedClients.map((c) => (
              <Fragment key={c.id}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "7px 2px", borderBottom: "1px solid #F1F0EC", fontSize: 12.5 }}>
                <span style={{ minWidth: 0 }}>
                  <b>{c.first_name} {c.last_name}</b>
                  <div style={{ fontSize: 11.5, color: MUTED, marginTop: 1 }}>
                    {(c.weeks || []).length}/{c.total_weeks} wks · paid {money(c.paid_total)}
                  </div>
                  {c.office_name && (
                    <div style={{ fontSize: 11.5, color: MUTED, marginTop: 1 }} data-testid={`qualified-office-${c.id}`}>{c.office_name}{c.pay_email ? ` · ${c.pay_email}` : ""}</div>
                  )}
                  <div style={{ fontFamily: "ui-monospace, monospace", color: NAVY, fontSize: 11.5, marginTop: 1 }}>{c.code_display}</div>
                </span>
                <span style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    className={`ref-mini ${faxClient === c.code ? "solid" : ""}`}
                    onClick={() => selectForFax(c)}
                    data-testid={`qualified-select-${c.id}`}
                  >{faxClient === c.code ? "✓ Selected" : "Select"}</button>
                  <button className="ref-mini" onClick={() => setEditing({ ...c })} data-testid={`qualified-edit-${c.id}`}>✎ Edit</button>
                  <button className="ref-mini" style={{ color: RED }} onClick={() => deleteClient(c)} data-testid={`qualified-delete-${c.id}`}>Delete</button>
                </span>
              </div>
              {/* edit dialog opens right under THIS client */}
              {editing && editing.status === "approved" && editing.id === c.id && renderEditDialog()}
              </Fragment>
            ))}
          </div>
        )}
        {clientsTab === "pending" && pendingClients.length === 0 && (
          <div style={{ fontSize: 12, color: MUTED }} data-testid="no-pending">No pending clients.</div>
        )}
        {clientsTab === "pending" && pendingClients.length > 0 && (
        <div style={listScroll} data-testid="pending-clients-list">
        {pendingClients.map((c) => (
          <Fragment key={c.id}>
          <div style={{ padding: "7px 2px", borderBottom: "1px solid #F1F0EC", fontSize: 12.5 }} data-testid={`pending-row-${c.id}`}>
            <b>{c.first_name} {c.last_name}</b>
            <div style={{ fontSize: 11.5, color: "#9A6208", marginTop: 1, fontWeight: 600 }}>
              Pending since {fmtDate(c.pending_at)} — awaiting follow-up
            </div>
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 1 }}>{c.office_name || c.clinic?.clinic_name}</div>
            <div style={{ fontFamily: "ui-monospace, monospace", color: NAVY, fontSize: 11.5, marginTop: 1 }}>{c.code_display}</div>
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <button className="ref-mini" style={{ flex: 1, justifyContent: "center" }}
                onClick={() => reopenRegistration(c)} data-testid={`pending-reopen-${c.id}`}>Back to review</button>
              <button className="ref-mini" style={{ flex: 1, justifyContent: "center" }}
                onClick={() => setEditing({ ...c })} data-testid={`pending-edit-${c.id}`}>✎ Edit</button>
              <button className="ref-mini" style={{ flex: 1, justifyContent: "center", color: RED }}
                onClick={() => deleteClient(c)} data-testid={`pending-delete-${c.id}`}>Delete</button>
            </div>
            <button
              className={`ref-mini ${faxClient === c.code ? "solid" : ""}`}
              style={{ width: "100%", marginTop: 6, justifyContent: "center" }}
              onClick={() => selectForFax(c)}
              data-testid={`pending-select-${c.id}`}
            >{faxClient === c.code ? "✓ Selected — ready to fax below" : "Select for fax"}</button>
          </div>
          {/* edit dialog opens right under THIS client */}
          {editing && editing.status === "pending" && editing.id === c.id && renderEditDialog()}
          </Fragment>
        ))}
        </div>
        )}
        {clientsTab === "denied" && deniedClients.length === 0 && (
          <div style={{ fontSize: 12, color: MUTED }} data-testid="no-denied">No denied clients.</div>
        )}
        {clientsTab === "denied" && deniedClients.length > 0 && (
        <div style={listScroll} data-testid="denied-clients-list">
        {deniedClients.map((c) => (
          <Fragment key={c.id}>
          <div style={{ padding: "7px 2px", borderBottom: "1px solid #F1F0EC", fontSize: 12.5 }} data-testid={`denied-row-${c.id}`}>
            <b>{c.first_name} {c.last_name}</b>
            <div style={{ fontSize: 11.5, color: RED, marginTop: 1, fontWeight: 600 }}>
              Denied {fmtDate(c.denied_at)} — does not qualify
            </div>
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 1 }}>{c.office_name || c.clinic?.clinic_name}</div>
            <div style={{ fontFamily: "ui-monospace, monospace", color: NAVY, fontSize: 11.5, marginTop: 1 }}>{c.code_display}</div>
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <button className="ref-mini" style={{ flex: 1, justifyContent: "center" }}
                onClick={() => reopenRegistration(c)} data-testid={`denied-reopen-${c.id}`}>Back to review</button>
              <button className="ref-mini" style={{ flex: 1, justifyContent: "center" }}
                onClick={() => setEditing({ ...c })} data-testid={`denied-edit-${c.id}`}>✎ Edit</button>
              <button className="ref-mini" style={{ flex: 1, justifyContent: "center", color: RED }}
                onClick={() => deleteClient(c)} data-testid={`denied-delete-${c.id}`}>Delete</button>
            </div>
            <button
              className={`ref-mini ${faxClient === c.code ? "solid" : ""}`}
              style={{ width: "100%", marginTop: 6, justifyContent: "center" }}
              onClick={() => selectForFax(c)}
              data-testid={`denied-select-${c.id}`}
            >{faxClient === c.code ? "✓ Selected — ready to fax below" : "Select for fax"}</button>
          </div>
          {/* edit dialog opens right under THIS client */}
          {editing && editing.status === "denied" && editing.id === c.id && renderEditDialog()}
          </Fragment>
        ))}
        </div>
        )}
      </Section>

      {/* ── 3 · client update faxes ── */}
      <Section
        title="Send client update by fax"
        testId="referrals-client-fax"
      >
        {faxClientObj ? (
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
            background: "rgba(31,78,121,.06)", border: "1px solid rgba(31,78,121,.25)",
            borderRadius: 10, padding: "9px 12px", fontSize: 12, marginBottom: 12,
          }} data-testid="fax-destination">
            <span style={{ minWidth: 0 }}>
              <b>{faxClientObj.first_name} {faxClientObj.last_name}</b>
              <div style={{ fontFamily: "ui-monospace, monospace", color: NAVY, fontSize: 11.5, marginTop: 1 }}>{faxClientObj.code_display}</div>
              <div style={{ color: MUTED, marginTop: 2 }}>Fax: <b style={{ color: NAVY }}>{faxClientObj.clinic?.fax_display}</b></div>
            </span>
            <button className="ref-mini" style={{ flexShrink: 0 }} onClick={() => setFaxClient("")} data-testid="fax-client-clear">✕ Clear</button>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: MUTED, marginBottom: 12 }} data-testid="fax-no-client">
            Search above and tap <b>Select</b> on any client — qualified, pending, denied or under review.
          </div>
        )}

        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 12, overflow: "hidden", marginBottom: 10 }}>
          {FAX_TEMPLATES.map((t) => (
            <div key={t.key} className="ref-radio"
              onClick={() => setFaxTemplate(t.key)}
              data-testid={`fax-template-${t.key}`}
              style={{ background: faxTemplate === t.key ? "rgba(15,59,46,.05)" : "#fff" }}>
              <div style={{
                width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
                border: faxTemplate === t.key ? 0 : `1.5px solid ${BORDER}`,
                background: faxTemplate === t.key ? GREEN : "#fff", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900,
              }}>{faxTemplate === t.key ? "✓" : ""}</div>
              <span style={{ fontSize: 12.5 }}><b>{t.label}</b> — {t.desc}</span>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input style={faxInput} placeholder="Test fax number — e.g. (604) 555-0100" value={testNumber}
            onChange={(e) => setTestNumber(e.target.value)} data-testid="testfax-number" />
          <button style={testBtn} disabled={faxBusy || !faxClientObj}
            onClick={() => sendTestFax(faxTemplate, testNumber, faxClient, undefined)}
            data-testid="testfax-send">{faxLbl("test", "Sending…", "Send test fax")}</button>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ ...testBtn, flex: 1, width: "auto" }} disabled={faxBusy || !faxClientObj}
            onClick={() => previewFax(faxTemplate, faxClient, undefined)} data-testid="fax-preview">{faxLbl("preview", "Preparing preview…", "Preview fax")}</button>
          <button style={{ ...sendBtn, flex: 1, width: "auto" }} disabled={faxBusy || !faxClientObj}
            onClick={() => sendFax(faxTemplate, faxClientObj?.clinic?.code, faxClient)} data-testid="fax-send">{faxLbl("send", "Sending…", "Send fax now")}</button>
        </div>
      </Section>
      </Group>

      {/* ══ GROUP 2 · clinicians ══ */}
      <Group
        title="Clinicians"
        sub="Add clinicians, manage and send promo."
        testId="referrals-group-clinicians"
      >
      <Section
        testId="referrals-physicians"
        flush
      >
        {/* add physician — paste buttons on the heading row, then roomy stacked fields */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".12em" }} data-testid="phys-form-title">
            {editingRow ? "Edit physician" : "Add physician"}
          </span>
          <span style={{ display: "flex", gap: 6 }}>
            {editingRow ? (
              <button className="ref-mini" onClick={cancelRowEdit} data-testid="phys-edit-top-cancel">✕ Cancel</button>
            ) : (
              <>
                <button className="ref-mini" onClick={() => pasteFromClipboard("name")} data-testid="cpso-paste-name">⇋ Physician</button>
                <button className="ref-mini" onClick={() => pasteFromClipboard("addr")} data-testid="cpso-paste-addr">⇋ Address</button>
              </>
            )}
          </span>
        </div>

        {pasteFallback && (
          <textarea
            style={{ ...inp, height: 72, paddingTop: 10, resize: "vertical", fontSize: 12, marginBottom: 8 }}
            placeholder={pasteFallback === "name"
              ? "Clipboard blocked — paste the physician name + CPSO# here"
              : "Clipboard blocked — paste the address / phone / fax block here"}
            onChange={onFallbackPaste}
            data-testid="cpso-paste-fallback"
          />
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
          {/* specialty picker — searchable, options can be added/deleted */}
          <div style={{ flex: "1 1 100%", position: "relative" }}>
            <button type="button" onClick={() => { setSpecOpen(!specOpen); setSpecQ(""); }} data-testid="phys-specialty"
              style={{ ...inp, width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", color: newPhys.specialty ? "#1C2B26" : "#94A3B8", fontWeight: newPhys.specialty ? 600 : 400, background: "#fff" }}>
              <span>{newPhys.specialty || "Specialty"}</span><span style={{ fontSize: 10, color: MUTED }}>▼</span>
            </button>
            {specOpen && (
              <>
                <div onClick={() => setSpecOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 25 }} data-testid="phys-specialty-backdrop" />
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 30, background: "#fff", border: `1.5px solid ${NAVY}`, borderRadius: 10, overflow: "hidden", boxShadow: "0 10px 28px rgba(31,78,121,.16)" }} data-testid="phys-specialty-menu">
                  <div style={{ padding: 8, borderBottom: `1px solid ${BORDER}` }}>
                    <input autoFocus style={{ ...inp, height: 34, fontSize: 12.5 }} placeholder="Search specialties…" value={specQ}
                      onChange={(e) => setSpecQ(e.target.value)} data-testid="phys-specialty-search" />
                  </div>
                  <div style={{ maxHeight: 190, overflowY: "auto" }}>
                    {newPhys.specialty && (
                      <div style={{ borderBottom: "1px solid #F1F5F9" }}>
                        <button type="button" onClick={() => { setNewPhys({ ...newPhys, specialty: "" }); setSpecOpen(false); }} data-testid="spec-opt-none"
                          style={{ width: "100%", textAlign: "left", border: 0, background: "none", padding: "9px 12px", fontSize: 12.5, cursor: "pointer", color: MUTED, fontStyle: "italic" }}>— No specialty —</button>
                      </div>
                    )}
                    {specialties.filter((s) => s.toLowerCase().includes(specQ.trim().toLowerCase())).map((s) => (
                      <div key={s} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 4px 0 12px", borderBottom: "1px solid #F1F5F9" }}>
                        <button type="button" onClick={() => { setNewPhys({ ...newPhys, specialty: s }); setSpecOpen(false); }} data-testid={`spec-opt-${s}`}
                          style={{ flex: 1, textAlign: "left", border: 0, background: "none", padding: "9px 0", fontSize: 13, cursor: "pointer", fontWeight: newPhys.specialty === s ? 700 : 400, color: newPhys.specialty === s ? GREEN : "#1C2B26" }}>{s}</button>
                        <button type="button" title="Delete option" onClick={() => deleteSpecialty(s)} data-testid={`spec-del-${s}`}
                          style={{ border: 0, background: "none", color: "#B0B8BF", fontSize: 12, cursor: "pointer", padding: "9px 8px" }}>✕</button>
                      </div>
                    ))}
                    {specialties.filter((s) => s.toLowerCase().includes(specQ.trim().toLowerCase())).length === 0 && (
                      <div style={{ padding: "10px 12px", fontSize: 12, color: MUTED }}>No match — add it below.</div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, padding: 8, background: "#F8FAFC" }}>
                    <input style={{ ...inp, height: 34, fontSize: 12.5 }} placeholder="+ Add a specialty — e.g. Hepatology" value={specNew}
                      onChange={(e) => setSpecNew(e.target.value)} data-testid="phys-specialty-new"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSpecialty(); } }} />
                    <button type="button" onClick={addSpecialty} data-testid="phys-specialty-add"
                      style={{ height: 34, padding: "0 14px", border: 0, borderRadius: 8, background: GREEN, color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0, cursor: "pointer" }}>Add</button>
                  </div>
                </div>
              </>
            )}
          </div>
          {/* promo offer — two buttons filling the row, directly under specialty */}
          <div style={{ display: "flex", flex: "1 1 100%", border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden", background: "#fff" }} data-testid="phys-rate-toggle">
            {[{ r: 75, lbl: "$75 a week" }, { r: 100, lbl: "$100 a week" }].map((o) => (
              <button key={o.r} type="button"
                onClick={() => setNewPhys({ ...newPhys, promo_rate: o.r })}
                data-testid={`phys-rate-${o.r}`}
                style={{
                  flex: 1, height: 38, border: 0, cursor: "pointer", fontSize: 12, fontWeight: 700,
                  borderRight: o.r === 75 ? `1px solid ${BORDER}` : 0,
                  background: (newPhys.promo_rate || 75) === o.r ? GREEN : "#fff",
                  color: (newPhys.promo_rate || 75) === o.r ? "#fff" : MUTED,
                }}>{o.lbl}</button>
            ))}
          </div>
          <input style={{ ...inp, flex: "1 1 100%", ...(physMiss.name && !newPhys.name.trim() ? { border: `1.5px solid ${RED}` } : {}) }}
            placeholder="Physician name *" value={newPhys.name}
            onChange={(e) => setNewPhys({ ...newPhys, name: e.target.value })} data-testid="phys-name" />
          <input style={{ ...inp, flex: "1 1 100%" }} placeholder="Clinic" value={newPhys.clinic}
            onChange={(e) => setNewPhys({ ...newPhys, clinic: e.target.value })} data-testid="phys-clinic" />
          <div style={{ display: "flex", gap: 8, flex: "1 1 100%" }}>
            <input style={{ ...inp, flex: 1, ...(physMiss.fax && !newPhys.fax.trim() ? { border: `1.5px solid ${RED}` } : {}) }}
              placeholder="Fax *" value={newPhys.fax}
              onChange={(e) => setNewPhys({ ...newPhys, fax: fmtPhone(e.target.value) })} data-testid="phys-fax" />
            <input style={{ ...inp, flex: 1 }} placeholder="Phone" value={newPhys.phone}
              onChange={(e) => setNewPhys({ ...newPhys, phone: fmtPhone(e.target.value) })} data-testid="phys-phone" />
          </div>
          <input style={{ ...inp, flex: "1 1 100%" }} placeholder="Email" value={newPhys.email}
            onChange={(e) => setNewPhys({ ...newPhys, email: e.target.value })} data-testid="phys-email" />
          <input style={{ ...inp, flex: "1 1 100%" }} placeholder="Street" value={newPhys.street}
            onChange={(e) => setNewPhys({ ...newPhys, street: e.target.value })} data-testid="phys-street" />
          <div style={{ display: "flex", gap: 8, flex: "1 1 100%" }}>
            <input style={{ ...inp, flex: 1 }} placeholder="City" value={newPhys.city}
              onChange={(e) => setNewPhys({ ...newPhys, city: e.target.value })} data-testid="phys-city" />
            <input style={{ ...inp, flex: 1 }} placeholder="Province" value={newPhys.province}
              onChange={(e) => setNewPhys({ ...newPhys, province: e.target.value })} data-testid="phys-province" />
          </div>
          <div style={{ display: "flex", gap: 8, flex: "1 1 100%" }}>
            <input style={{ ...inp, flex: 1 }} placeholder="Postal code" value={newPhys.postal}
              onChange={(e) => setNewPhys({ ...newPhys, postal: e.target.value })} data-testid="phys-postal" />
            <input style={{ ...inp, flex: 1, ...(cpsoDup ? { border: `1.5px solid ${RED}` } : {}) }} placeholder="CPSO #" value={newPhys.cpso}
              onChange={(e) => setNewPhys({ ...newPhys, cpso: e.target.value })} data-testid="phys-cpso" />
          </div>
          {cpsoDup && (
            <div style={{ border: `1.5px solid ${RED}`, borderRadius: 12, padding: "12px 14px", background: "rgba(180,53,27,.04)", flex: "1 1 100%", lineHeight: 1.5 }} data-testid="phys-cpso-conflict">
              <b style={{ fontSize: 12.5, color: RED, display: "block", marginBottom: 6 }}>⚠ This licence number is already in our system</b>
              <div style={{ fontSize: 12.5, color: "#1C2B26" }}>
                Registered to: <b>{cpsoDup.name}</b>
                {cpsoDup.clinic ? <><br />{cpsoDup.clinic}</> : null}
                {cpsoDup.phone ? <><br />Phone: {cpsoDup.phone}</> : null}
                {cpsoDup.fax ? <><br />Fax: {cpsoDup.fax}</> : null}
                <br /><span style={{ color: MUTED, fontSize: 11.5 }}>Use Cancel below to clear the form, or edit the existing record instead.</span>
              </div>
            </div>
          )}
          {physFaxConflict && (
            <div style={{ border: `1.5px solid ${RED}`, borderRadius: 12, padding: "12px 14px", background: "rgba(180,53,27,.04)", flex: "1 1 100%", lineHeight: 1.5 }} data-testid="phys-fax-conflict">
              <b style={{ fontSize: 12.5, color: RED, display: "block", marginBottom: 6 }}>⚠ This fax number is already in our system</b>
              <div style={{ fontSize: 12.5, color: "#1C2B26" }}>
                Registered to: <b>{physFaxConflict.physician_name}</b>
                {physFaxConflict.code_display ? (<>
                  <br />Clinic code: <span style={{ fontFamily: "ui-monospace, monospace", fontWeight: 700, color: NAVY }}>{physFaxConflict.code_display}</span>
                </>) : null}
                {physFaxConflict.address ? (<>
                  <br />{physFaxConflict.address.split(",")[0]}
                  {physFaxConflict.address.includes(",") ? <><br />{physFaxConflict.address.split(",").slice(1).join(",").trim()}</> : null}
                </>) : null}
                {physFaxConflict.phone ? <><br />Phone: {physFaxConflict.phone}</> : null}
                {physFaxConflict.fax_display ? <><br />Fax: {physFaxConflict.fax_display}</> : null}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button style={{ ...sendBtn, flex: 1 }} onClick={() => addPhysician(true)} data-testid="phys-fax-confirm">Add as new record</button>
                <button style={{ ...abtn, flex: 1, justifyContent: "center" }} onClick={() => setPhysFaxConflict(null)} data-testid="phys-fax-cancel">Cancel</button>
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, flex: "1 1 100%" }}>
            <button style={{ ...sendBtn, flex: 1, width: "auto" }} onClick={() => addPhysician(false)} data-testid="phys-add">{editingRow ? "Save changes" : "Add physician"}</button>
            <button type="button" style={{ ...abtn, flexShrink: 0, justifyContent: "center" }} onClick={cancelPhysicianForm} data-testid="phys-cancel">Cancel</button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input style={{ ...searchInp, flex: 1 }} placeholder="Search name, clinic or fax…" value={physQ}
            onChange={(e) => setPhysQ(e.target.value)} data-testid="physician-search" />
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <select value={specFilter} onChange={(e) => setSpecFilter(e.target.value)} data-testid="physician-spec-filter"
            style={{ ...searchInp, flex: 1, minWidth: 0, fontWeight: 600, color: specFilter ? NAVY : MUTED, background: "#fff", cursor: "pointer" }}>
            <option value="">All specialties</option>
            {specialties.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <label style={{ ...testBtn, height: 34, fontSize: 11.5, width: 118, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }} data-testid="csv-import-btn">
            Import CSV
            <input type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={importCsv} />
          </label>
        </div>

        {/* contact-list tabs — Promoting/Added/Removed for manual physicians,
            Active/Inactive for self-registered clinics, No contact for opt-outs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
          {[
            ["promoting", `Promoting (${filteredPhys.length})`, NAVY],
            ["added", `Added (${filteredAdded.length})`, SUCCESS],
            ["removed", `Removed (${filteredRemoved.length})`, "#3E4C46"],
            ["active", `Active (${filteredActive.length})`, GREEN],
            ["inactive", `Inactive (${filteredInactive.length})`, MUTED],
            ["optout", `No contact (${filteredOptouts.length})`, RED],
          ].map(([key, label, color]) => (
            <button key={key}
              style={{ ...abtn, flex: "1 1 30%", height: 34, justifyContent: "center", fontSize: 10.5, whiteSpace: "nowrap", ...(physTab === key ? { background: color, color: "#fff", border: `1px solid ${color}` } : {}) }}
              onClick={keepScroll("[data-testid='phys-tab-promoting']")(() => setPhysTab(key))} data-testid={`phys-tab-${key}`}>{label}</button>
          ))}
        </div>

        {physTab === "promoting" && physTable(filteredPhys, "promoting", "No physicians match.", promotingActions)}
        {physTab === "added" && physTable(filteredAdded, "added", "No added physicians yet.", addedActions)}
        {physTab === "removed" && physTable(filteredRemoved, "removed", "Nothing removed.", removedActions)}
        {physTab === "active" && physTable(filteredActive, "active", "No active clinics yet.", activeActions)}
        {physTab === "inactive" && physTable(filteredInactive, "inactive", "No inactive clinics.", inactiveActions)}
        {physTab === "optout" && physTable(filteredOptouts, "optout", "No one on the do-not-contact list.", optoutActions)}

        {promoPhysObj && (
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
            background: "rgba(31,78,121,.06)", border: "1px solid rgba(31,78,121,.25)",
            borderRadius: 10, padding: "9px 12px", fontSize: 12, marginTop: 12,
          }} data-testid="promo-destination">
            <span style={{ minWidth: 0 }}>
              <b>{promoPhysObj.name}</b>
              <div style={{ color: MUTED, marginTop: 2 }}>${promoPhysObj.promo_rate || 75} a week — preview, download and test fax use this rate</div>
            </span>
            <button className="ref-mini" style={{ flexShrink: 0 }} onClick={() => setPromoPhysId(null)} data-testid="promo-clear">✕ Clear</button>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 12, marginBottom: 10 }}>
          <input style={faxInput} placeholder="Test fax number — e.g. (604) 555-0100" value={promoTestNumber}
            onChange={(e) => setPromoTestNumber(e.target.value)} data-testid="promo-testfax-number" />
          <button style={testBtn} disabled={faxBusy}
            onClick={() => sendTestFax("promo", promoTestNumber, undefined, undefined, promoPhysObj?.promo_rate)}
            data-testid="promo-testfax-send">{faxLbl("test", "Sending…", "Send test fax")}</button>
        </div>
        <div data-testid="promo-suppression" style={{
          display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center",
          marginTop: 12, padding: "9px 12px", borderRadius: 10,
          border: `1px solid ${BORDER}`, background: "white",
        }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, color: MUTED, textTransform: "uppercase", marginRight: 2 }}>
            Resend rule
          </span>
          {[
            { v: "days", label: "Skip recently sent" },
            { v: "all", label: "Skip everyone already sent" },
            { v: "none", label: "Send to everyone" },
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => setFaxSuppression(o.v)}
              data-testid={`promo-suppression-${o.v}`}
              style={{
                padding: "5px 11px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                border: `1.5px solid ${faxSuppression === o.v ? "#1F4E79" : BORDER}`,
                background: faxSuppression === o.v ? "#1F4E79" : "white",
                color: faxSuppression === o.v ? "white" : "#1F4E79",
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              {o.label}
            </button>
          ))}
          {faxSuppression === "days" && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: MUTED }}>
              last
              <input
                type="text" inputMode="numeric" pattern="[0-9]*" value={faxSuppressionDays}
                onChange={(e) => setFaxSuppressionDays(e.target.value.replace(/\D/g, "").slice(0, 3))}
                onBlur={() => setFaxSuppressionDays(String(faxDaysNum))}
                data-testid="promo-suppression-days"
                style={{
                  width: 54, padding: "3px 6px", borderRadius: 7,
                  border: `1px solid ${BORDER}`, fontSize: 12.5, fontFamily: "inherit",
                }}
              />
              days
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button style={{ ...testBtn, flex: 1, width: "auto" }} disabled={faxBusy}
            onClick={() => previewFax("promo", undefined, undefined, promoPhysObj?.promo_rate)} data-testid="promo-preview">{faxLbl("preview", "Preparing preview…", "Preview")}</button>
          <button style={{ ...testBtn, flex: 1, width: "auto" }} disabled={faxBusy}
            onClick={() => downloadPromoPdf(promoPhysObj?.promo_rate)} data-testid="promo-download">{faxLbl("download", "Preparing preview…", "Download")}</button>
          <button style={{ ...sendBtn, flex: 1, width: "auto" }} disabled={faxBusy}
            onClick={broadcast} data-testid="promo-broadcast">{faxLbl("broadcast", "Sending…", `Send (${promotingPhys.length})`)}</button>
        </div>
      </Section>
      </Group>

      {/* ══ GROUP 3 · fax confirmations ══ */}
      <Group
        title="Fax confirmations"
        sub="Delivery status for every fax sent this period."
        testId="referrals-group-faxes"
      >
      <Section
        title="Fax activity"
        aside={
          <button
            style={{ ...abtn, flex: "0 0 auto", width: 30, height: 28, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
            onClick={refreshFaxes} disabled={faxSyncing}
            title="Refresh delivery statuses" aria-label="Refresh delivery statuses"
            data-testid="fax-refresh"
          ><RefreshCw size={13} className={faxSyncing ? "ref-spin" : ""} style={{ display: "block" }} /></button>
        }
        testId="referrals-fax-log"
        flush
      >
        {/* Day + Month selectors — these drive the fax list below (and the
            payout quadrants at the top of the tab) */}
        <div className="inc-team-month" data-testid="ref-team-strip" style={{ marginBottom: 10 }}>
          <select
            data-testid="ref-day-select"
            className="inc-day-select"
            value={day}
            onChange={(e) => setDay(e.target.value)}
          >
            <option value="all" style={{ color: "#000" }}>All days</option>
            {dayOptions.map((d) => (
              <option key={d} value={d} style={{ color: "#000" }}>
                {fromYMD(d) ? format(fromYMD(d), "MMM d") : d}
              </option>
            ))}
          </select>
          <select
            data-testid="ref-month-select"
            className="inc-month-select"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {months.map((m) => (
              <option key={m.key} value={m.key} style={{ color: "#000" }}>{m.label}</option>
            ))}
          </select>
        </div>

        {/* search across everything visible on a row — same field style as the physician search */}
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <input style={{ ...searchInp, flex: 1 }} placeholder="Search faxes — name, code, clinic, number…"
            value={faxQ} onChange={(e) => setFaxQ(e.target.value)} data-testid="fax-search" />
        </div>

        {/* category tabs — same pattern as the physician list */}
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          {[["all", "All"], ["patient", "Patient"], ["promotion", "Promo"]].map(([k, lbl]) => (
            <button key={k}
              style={{ ...abtn, flex: 1, minWidth: 0, height: 34, justifyContent: "center", fontSize: 10.5, whiteSpace: "nowrap", overflow: "hidden", ...(faxCat === k ? { background: NAVY, color: "#fff", border: `1px solid ${NAVY}` } : {}) }}
              onClick={keepScroll("[data-testid='fax-cat-all']")(() => setFaxCat(k))}
              data-testid={`fax-cat-${k}`}>{lbl} ({catCounts[k]})</button>
          ))}
        </div>

        {/* filter row — status pills stretch the full width (period comes from
            the shared Day + Month selectors at the top of the tab) */}
        <div style={{ display: "flex", gap: 6, alignItems: "stretch", flexWrap: "nowrap", marginBottom: 10 }}>
          {["Sent", "Failed", "In Progress"].map((s) => {
            const col = FAX_STATUS_COLORS[s];
            const on = faxStatusFilter === s;
            return (
              <button key={s}
                style={{ ...abtn, flex: 1, minWidth: 0, justifyContent: "center", padding: "4px 2px", fontSize: 10.5, height: 34, whiteSpace: "nowrap", overflow: "hidden", border: `1.5px solid ${col}`, color: on ? "#fff" : col, background: on ? col : "#fff" }}
                onClick={keepScroll("[data-testid='fax-filter-sent']")(() => setFaxStatusFilter(on ? null : s))}
                data-testid={`fax-filter-${s.toLowerCase().replace(" ", "-")}`}>
                {FAX_STATUS_LABEL[s] || s} ({faxCounts[s]})
              </button>
            );
          })}
        </div>
        <div ref={faxRowsRef} style={{ minHeight: visibleFaxes.length ? Math.min(faxRowsMinH, 420) || undefined : undefined, ...listScroll }}>
        {visibleFaxes.length === 0 && (
          <div style={{ fontSize: 12, color: MUTED }} data-testid="no-faxes">
            {faxFiltersNeutral ? "No faxes for this period." : "No faxes match these filters."}
          </div>
        )}
        {visibleFaxes.map((f) => {
          const isPatient = f.template !== "promo";
          return (
          <div key={f.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 2px", borderBottom: "1px solid #F1F0EC", fontSize: 12 }} data-testid={`fax-row-${f.id}`}>
            <span style={{ minWidth: 0, lineHeight: 1.55 }}>
              <b style={{ textTransform: "capitalize", display: "block" }}>
                {f.template}
                {f.kind === "test" ? <span style={{ color: AMBER, fontWeight: 700 }}> · test</span> : null}
              </b>
              {isPatient && f.client_name ? (
                <>
                  <span style={{ color: TEXT, fontWeight: 600, display: "block" }}>{f.client_name}</span>
                  {f.client_code_display ? (
                    <span style={{ fontFamily: "ui-monospace, monospace", color: NAVY, fontSize: 11, display: "block" }}>{f.client_code_display}</span>
                  ) : null}
                  {f.to_name ? <span style={{ color: MUTED, display: "block" }}>{f.to_name}</span> : null}
                  <span style={{ color: MUTED, display: "block", fontSize: 11.5 }}>{f.to_number}</span>
                  <span style={{ color: MUTED, display: "block", fontSize: 11 }} data-testid={`fax-meta-${f.id}`}>
                    {fmtDate(f.created_at)}
                    {(f.account_code || f.srfax_id) ? <><span className="ref-fax-sep"> · </span><span className="ref-fax-ref">Ref #{f.account_code || f.srfax_id}</span></> : null}
                  </span>
                </>
              ) : (
                <>
                  {f.to_name ? <span style={{ color: MUTED, display: "block" }}>{faxRecipient(f.to_name)}</span> : null}
                  <span style={{ color: MUTED, display: "block" }}>{f.to_number}</span>
                  <span style={{ color: MUTED, display: "block", fontSize: 11 }} data-testid={`fax-meta-${f.id}`}>
                    {fmtDate(f.created_at)}
                    {(f.account_code || f.srfax_id) ? <><span className="ref-fax-sep"> · </span><span className="ref-fax-ref">Ref #{f.account_code || f.srfax_id}</span></> : null}
                  </span>
                </>
              )}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              {f.status === "Failed" && (
                <button className="ref-mini" style={{ height: 24, padding: "0 8px", fontSize: 10, display: "inline-flex", alignItems: "center" }}
                  onClick={() => { setRetryFax(f); setRetryNumber(f.to_number || ""); }}
                  data-testid={`fax-retry-${f.id}`}>↻ Retry</button>
              )}
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "0 8px", borderRadius: 999, height: 24,
                display: "inline-flex", alignItems: "center",
                background: f.status === "Sent" ? "rgba(46,125,91,.12)" : f.status === "Failed" ? "rgba(180,53,27,.10)" : "rgba(229,138,46,.14)",
                color: f.status === "Sent" ? SUCCESS : f.status === "Failed" ? RED : "#9A6208",
              }}>{FAX_STATUS_LABEL[f.status] || f.status}</span>
              <button className="ref-mini" style={{ color: RED, height: 24, width: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                onClick={() => deleteFax(f)}
                title="Delete this fax log" aria-label="Delete this fax log"
                data-testid={`fax-delete-${f.id}`}>✕</button>
            </span>
          </div>
          );
        })}
        </div>

        {retryFax && (
          <div style={{ ...rec, background: "#FAFAF7" }} data-testid="fax-retry-dialog">
            <b style={{ fontSize: 13, textTransform: "capitalize" }}>Re-send — {retryFax.template} fax to {retryFax.to_name}</b>
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>Fix the number if needed, then send again.</div>
            <div style={{ ...acts, alignItems: "center", flexWrap: "wrap" }}>
              <input style={{ ...inp, flex: "1 1 180px" }} value={retryNumber} onChange={(e) => setRetryNumber(e.target.value)}
                placeholder="Fax number" data-testid="fax-retry-number" />
              <button style={{ ...abtn, background: NAVY, color: "#fff", border: `1px solid ${NAVY}` }} onClick={resendFax} data-testid="fax-retry-send">Send again</button>
              <button style={abtn} onClick={() => setRetryFax(null)} data-testid="fax-retry-cancel">Cancel</button>
            </div>
          </div>
        )}
      </Section>
      </Group>
    </div>
  );
}
